<template>
  <!-- ═══════════ Mobile / tablet-portrait (<960): findings list → slide-up detail sheet ═══════════ -->
  <div v-if="isMobile" class="anom-m">
    <header class="am-top">
      <button type="button" class="am-back" aria-label="Back" @click="onBack"><v-icon size="22">mdi-chevron-left</v-icon></button>
      <div class="am-title">
        <span class="am-title-main">Findings</span>
        <span class="am-title-sub">{{ headingTitle }} · {{ statusCounts.all }} found</span>
      </div>
      <button v-if="can(Permissions.AnomalyView)" type="button" class="am-top-report" title="Generate report"
        @click="openReportDialog"><v-icon size="20">mdi-file-chart-outline</v-icon></button>
    </header>

    <!-- Filters: status tabs + search + type -->
    <div class="am-filters">
      <div class="am-tabs">
        <button v-for="opt in tabFilters" :key="opt.value" type="button" class="am-tab"
          :class="{ active: statusFilter === opt.value }" @click="statusFilter = opt.value">
          <span>{{ opt.label }}</span>
          <span class="am-tab-count" :class="opt.value === 'all' ? '' : statusClass(opt.value)">{{ statusCounts[opt.value] }}</span>
        </button>
      </div>
      <div class="am-filter-row">
        <div class="am-search">
          <v-icon size="17" class="am-search-icon">mdi-magnify</v-icon>
          <input v-model="search" type="text" placeholder="Search findings" />
        </div>
        <select v-model="typeFilter" class="pill-select am-type-select">
          <option value="all">All types</option>
          <option v-for="t in typeOptions" :key="t" :value="t">{{ t }}</option>
        </select>
      </div>
      <TagFilterSelect v-if="tagOptions.length" v-model="tagFilter" :options="tagOptions" label="Tags" flat
        class="am-tag-filter" />
    </div>

    <!-- List -->
    <div class="am-list duallo-scroll">
      <template v-if="isLoading">
        <div v-for="n in 8" :key="n" class="am-item is-skeleton">
          <span class="sk am-item-thumb"></span>
          <span class="am-item-main">
            <span class="sk sk-line" style="width: 60%"></span>
            <span class="sk sk-line" style="width: 40%; height: 0.7rem; margin-top: 6px"></span>
          </span>
          <span class="sk sk-pill"></span>
        </div>
      </template>

      <template v-else-if="filteredRows.length">
        <button v-for="row in filteredRows" :key="row.id" type="button" class="am-item" @click="openEdit(row)">
          <span class="am-item-thumb">
            <img v-if="row.imageSrc" :src="row.imageSrc" alt="" @error="useDefaultImage" />
            <v-icon v-else size="20" color="#4a5a60">mdi-image-outline</v-icon>
          </span>
          <span class="am-item-main">
            <span class="am-item-name">
              <span class="am-item-dot" :style="{ background: typeColor(row.label) }"></span>
              <span class="am-item-name-text">{{ rowName(row) }}</span>
            </span>
            <span class="am-item-sub">{{ row.scanName }} · #{{ shortId(row.id) }}</span>
          </span>
          <span class="duallo-status-pill" :class="statusClass(row.status)">{{ row.status }}</span>
        </button>
      </template>

      <div v-else-if="allCaughtUp" class="am-done">
        <div class="am-done-icon"><v-icon size="30" color="#6ee7a0">mdi-check</v-icon></div>
        <p class="am-done-title">All caught up</p>
        <p class="am-done-sub">Every finding in this scope has been reviewed.</p>
        <button type="button" class="am-report" @click="openReportDialog">
          <v-icon size="18">mdi-file-chart-outline</v-icon><span>Generate report</span>
        </button>
      </div>

      <div v-else class="am-empty">
        <v-icon size="40" color="#4a5a60">mdi-clipboard-pulse-outline</v-icon>
        <p class="empty-title">No anomalies here</p>
        <p class="empty-subtitle">Try a different status filter, type, or search.</p>
      </div>
    </div>
  </div>

  <!-- ═══════════ Desktop / tablet workspace ═══════════ -->
  <div v-else class="anom">
    <!-- Return path: a breadcrumb whose segments are all navigable. -->
    <nav class="crumbs" aria-label="Breadcrumb">
      <button type="button" class="crumb crumb-link" @click="goHome">Assets</button>
      <v-icon size="14" class="crumb-sep">mdi-chevron-right</v-icon>
      <button v-if="isAssetLocked" type="button" class="crumb crumb-link" @click="onBack">{{ headingTitle }}</button>
      <span v-else class="crumb">{{ headingTitle }}</span>
      <v-icon size="14" class="crumb-sep">mdi-chevron-right</v-icon>
      <span class="crumb crumb-current">Findings</span>
    </nav>

    <!-- Workspace: LEFT = title + controls + list · RIGHT = detail + supporting data -->
    <div class="anom-body" :class="{ 'with-rail': showRail }">
      <section class="list-col">
        <div class="anom-head-row">
          <div class="anom-head-text">
            <h1 class="anom-title">Findings</h1>
            <p class="anom-count">{{ statusCounts.all }} found across {{ scanScopeLabel }} of {{ headingTitle }}</p>
          </div>
          <button type="button" class="report-btn" title="Generate a PDF report" @click="openReportDialog">
            <v-icon size="16">mdi-file-chart-outline</v-icon>
            <span>Generate report</span>
          </button>
        </div>

        <!-- Scope selectors (org-level entry only; hidden when locked to a single asset) -->
        <div v-if="!isAssetLocked" class="scope-bar">
          <label class="scope-field">
            <span class="scope-label">Site</span>
            <select v-model="selectedSiteId" class="scan-select">
              <option value="all">All sites</option>
              <option v-for="s in siteOptions" :key="s.id" :value="s.id">{{ s.title }}</option>
            </select>
          </label>
          <label class="scope-field">
            <span class="scope-label">Asset</span>
            <select v-model="selectedAssetId" class="scan-select">
              <option value="all">All assets</option>
              <option v-for="a in assetOptions" :key="a.id" :value="a.id">{{ a.name }}</option>
            </select>
          </label>
        </div>

        <!-- Status tabs + scan / type filters -->
        <div class="filter-bar">
          <div class="tab-group">
            <button v-for="opt in tabFilters" :key="opt.value" type="button" class="tab-btn"
              :class="{ active: statusFilter === opt.value }" @click="statusFilter = opt.value">
              <span>{{ opt.label }}</span>
              <span class="tab-count" :class="opt.value === 'all' ? '' : statusClass(opt.value)">{{ statusCounts[opt.value] }}</span>
            </button>
          </div>
          <select v-if="singleAssetScope" v-model="scanFilter" class="pill-select">
            <option value="all">All scans</option>
            <option v-for="scan in scanOptions" :key="scan.id" :value="scan.id">{{ scan.name }}</option>
          </select>
          <select v-model="typeFilter" class="pill-select">
            <option value="all">All types</option>
            <option v-for="t in typeOptions" :key="t" :value="t">{{ t }}</option>
          </select>
          <TagFilterSelect v-if="tagOptions.length" v-model="tagFilter" :options="tagOptions" label="All tags" flat
            class="tag-filter" style="flex: 0 0 160px;" />
        </div>

        <!-- Search (scoped to the list) -->
        <div class="search-wrap">
          <v-icon size="17" class="search-icon">mdi-magnify</v-icon>
          <input v-model="search" type="text" class="search-input" placeholder="Search findings" />
        </div>

        <!-- Bulk action bar -->
        <div v-if="selectedIds.length" class="bulk-bar">
          <span class="bulk-count">{{ selectedIds.length }} selected</span>
          <div class="bulk-actions">
            <button v-if="canReview" type="button" class="bulk-btn reject" :disabled="isReviewing" @click="bulkReview('Rejected')">Reject</button>
            <button v-if="canReview" type="button" class="bulk-btn approve" :disabled="isReviewing" @click="bulkReview('Approved')">Approve</button>
            <button type="button" class="bulk-btn ghost" @click="clearSelection">Clear</button>
          </div>
        </div>

      <section class="list-card">
        <div class="list-head">
          <span class="lh-check">
            <input type="checkbox" class="chk" :checked="allVisibleSelected" :disabled="!filteredRows.length" @change="toggleSelectAll" />
          </span>
          <span class="lh-anom">Anomaly</span>
          <span class="lh-ref">Reference</span>
          <span class="lh-status">Status</span>
        </div>

        <div v-if="isLoading" class="list-scroll duallo-scroll">
          <div v-for="n in 8" :key="n" class="anom-row is-skeleton">
            <span class="ar-check"></span>
            <span class="sk ar-thumb"></span>
            <span class="ar-main">
              <span class="sk sk-line" style="width: 60%"></span>
              <span class="sk sk-line" style="width: 40%; height: 0.7rem"></span>
            </span>
            <span class="ar-ref"><span class="sk sk-line" style="width: 50px"></span></span>
            <span class="ar-status"><span class="sk sk-pill"></span></span>
          </div>
        </div>

        <div v-else-if="!filteredRows.length" class="empty-state">
          <v-icon size="40" color="#4a5a60">mdi-clipboard-pulse-outline</v-icon>
          <p class="empty-title">No anomalies here</p>
          <p class="empty-subtitle">Try a different status filter, scan, or search.</p>
        </div>

        <div v-else ref="listScroll" class="list-scroll duallo-scroll">
          <button v-for="row in filteredRows" :key="row.id" type="button" class="anom-row"
            :class="{ 'is-current': currentId === row.id }" :data-id="row.id"
            @click="selectRow(row)" @mouseenter="hoveredRowId = row.id" @mouseleave="hoveredRowId = null">
            <span class="ar-check" @click.stop>
              <input type="checkbox" class="chk" :checked="isSelected(row.id)" @change="toggleSelect(row.id)" />
            </span>
            <span class="ar-thumb">
              <img v-if="row.imageSrc" :src="row.imageSrc" class="ar-thumb-img" alt="" @error="useDefaultImage" />
              <v-icon v-else size="18" color="#4a5a60">mdi-image-outline</v-icon>
            </span>
            <span class="ar-main">
              <span class="ar-name-row">
                <span class="ar-dot" :style="{ background: typeColor(row.label) }" :title="row.label || 'Anomaly'"></span>
                <span class="ar-name">{{ rowName(row) }}</span>
              </span>
              <span class="ar-sub">{{ row.scanName }} · #{{ shortId(row.id) }}</span>
            </span>
            <span class="ar-ref">
              <span v-if="row.externalReference" class="ref-text" :title="row.externalReference">{{ row.externalReference }}</span>
              <span v-else class="ref-empty">—</span>
            </span>
            <span class="ar-status">
              <span class="duallo-status-pill" :class="statusClass(row.status)">{{ row.status }}</span>
            </span>
          </button>
        </div>
      </section>
      </section>

      <!-- Tablet-landscape (960–1279): dim backdrop behind the pop-out review drawer. -->
      <div v-if="showRail && !isWideDesktop && detailOpen" class="anom-drawer-backdrop" @click="closeDetail"></div>

      <!-- Review surface: persistent two-pane at ≥1280; a dismissible right drawer at 960–1279. -->
      <aside v-if="showRail && (isWideDesktop || detailOpen)" class="anom-rail"
        :class="{ 'anom-rail--drawer': !isWideDesktop }">
        <button v-if="!isWideDesktop" type="button" class="anom-drawer-close" aria-label="Close" @click="closeDetail">
          <v-icon size="20">mdi-close</v-icon>
        </button>
        <template v-if="currentRow">
          <!-- One supporting element: source frame · details + edit + review + notes · spatial context -->
          <div class="support-panel">
            <div class="rc-frame">
              <img v-if="currentRow.imageSrc" :src="currentRow.imageSrc" class="rc-frame-img" alt="Anomaly source frame"
                @error="useDefaultImage" />
              <div v-else class="rc-frame-empty"><v-icon size="40" color="#4a5a60">mdi-image-off-outline</v-icon></div>
            </div>

            <!-- Media actions: always present; each disabled when not available for this finding. The
                 bar is attached to the image so it reads as part of it. -->
            <div class="rc-media-actions">
              <button type="button" class="rc-media-btn" :disabled="!currentRow.imageSrc"
                title="Download the finding image" @click="downloadAnomalyImage(currentRow)">
                <v-icon size="16">mdi-tray-arrow-down</v-icon><span>Download</span>
              </button>
              <button type="button" class="rc-media-btn" title="View the full AI source image"
                :disabled="provMeta.loading || provViewerLoading || !provMeta.hasProvenance"
                @click="openProvenanceImage(currentRow, 'source')">
                <v-progress-circular v-if="provMeta.loading" indeterminate size="15" width="2" color="#64ffda" />
                <v-icon v-else size="16">mdi-image-outline</v-icon><span>View Full Image</span>
              </button>
            </div>

            <div class="rc-detail">
              <div class="rc-detail-head">
                <div class="rc-detail-title">
                  <span class="rc-dot" :style="{ background: typeColor(currentRow.label) }"></span>
                  <!-- Name: click the pencil to edit inline; check saves, ✕ cancels. -->
                  <template v-if="canEditThisRow && editingField === 'name'">
                    <input v-model="editName" class="rc-inline-input rc-name-input" placeholder="Anomaly name"
                      @keyup.enter="saveDetails" @keyup.esc="cancelFieldEdit" />
                    <button type="button" class="rc-inline-btn save" :disabled="isSavingName" title="Save" @click="saveDetails">
                      <v-progress-circular v-if="isSavingName" indeterminate size="15" width="2" color="#6ee7a0" />
                      <v-icon v-else size="18">mdi-check</v-icon>
                    </button>
                    <button type="button" class="rc-inline-btn cancel" :disabled="isSavingName" title="Cancel" @click="cancelFieldEdit">
                      <v-icon size="18">mdi-close</v-icon>
                    </button>
                  </template>
                  <template v-else>
                    <h3 class="rc-name">{{ rowName(currentRow) }}</h3>
                    <button v-if="canEditThisRow" type="button" class="rc-edit-icon" title="Edit name"
                      @click="beginFieldEdit('name')">
                      <v-icon size="15">mdi-pencil-outline</v-icon>
                    </button>
                  </template>
                </div>
                <span class="duallo-status-pill" :class="statusClass(currentRow.status)">{{ currentRow.status }}</span>
              </div>

              <dl class="rc-meta">
                <div class="rc-meta-item"><dt>Type</dt><dd>{{ currentRow.label || 'Anomaly' }}</dd></div>
                <!-- Reference ID: col 2 of row 1, its own property, edited with the same pencil flow as the name. -->
                <div class="rc-meta-item rc-meta-ref">
                  <dt>Reference ID</dt>
                  <dd>
                    <template v-if="canEditThisRow && editingField === 'reference'">
                      <span class="rc-inline-row">
                        <input v-model="editReference" class="rc-inline-input" placeholder="e.g. work order / CMMS ID"
                          @keyup.enter="saveDetails" @keyup.esc="cancelFieldEdit" />
                        <button type="button" class="rc-inline-btn save" :disabled="isSavingName" title="Save" @click="saveDetails">
                          <v-progress-circular v-if="isSavingName" indeterminate size="14" width="2" color="#6ee7a0" />
                          <v-icon v-else size="17">mdi-check</v-icon>
                        </button>
                        <button type="button" class="rc-inline-btn cancel" :disabled="isSavingName" title="Cancel" @click="cancelFieldEdit">
                          <v-icon size="17">mdi-close</v-icon>
                        </button>
                      </span>
                    </template>
                    <span v-else class="rc-ref-value">
                      <span :class="{ muted: !currentRow.externalReference }">{{ currentRow.externalReference || '—' }}</span>
                      <button v-if="canEditThisRow" type="button" class="rc-edit-icon" title="Edit reference ID"
                        @click="beginFieldEdit('reference')">
                        <v-icon size="14">mdi-pencil-outline</v-icon>
                      </button>
                    </span>
                  </dd>
                </div>
                <!-- Row 2: Asset · Scan. -->
                <div v-if="!isAssetLocked" class="rc-meta-item"><dt>Asset</dt><dd>{{ currentRow.assetName }}</dd></div>
                <div class="rc-meta-item"><dt>Scan</dt><dd>{{ currentRow.scanName }}</dd></div>
                <div v-if="!isAssetLocked && currentRow.siteTitle" class="rc-meta-item"><dt>Site</dt><dd>{{ currentRow.siteTitle }}</dd></div>
              </dl>

              <div v-if="saveError && editingField" class="inline-error">
                <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
                <span>{{ saveError }}</span>
              </div>

              <!-- Review: buttons while pending (or after "Change decision"); otherwise a completed
                   summary with the option to re-open it — so a decision is never permanently locked. -->
              <div v-if="canReview" class="rc-review">
                <div v-if="isReviewOpen(currentRow)" class="rc-actions">
                  <button type="button" class="rc-btn reject" :disabled="isReviewing" @click="requestReject">Reject</button>
                  <button type="button" class="rc-btn approve" :disabled="isReviewing" @click="requestApprove">Approve</button>
                </div>
                <div v-else class="rc-reviewed">
                  <span class="rc-reviewed-text">
                    <v-icon size="17" :color="statusIconColor(currentRow.status)">{{ statusIcon(currentRow.status) }}</v-icon>
                    <span>Review complete — <strong>{{ currentRow.status }}</strong>.</span>
                  </span>
                  <button type="button" class="rc-change-btn" @click="reopenReview(currentRow)">Change decision</button>
                </div>
              </div>
            </div>

            <!-- Spatial context (plot plan). Side elevation removed — each marker now carries a
                 Low / Medium / High elevation band in its hover tooltip instead. -->
            <div v-if="showMinimap" class="support-map">
              <div class="mini-toolbar">
                <span class="mini-caption">Plot plan</span>
                <button type="button" class="mini-full" title="Fullscreen" @click="minimapFull = true">
                  <v-icon size="16">mdi-fullscreen</v-icon>
                </button>
              </div>
              <div class="mini-stage">
                <AnomalyMinimap title="" axis-label=""
                  :points="minimapPlan.points" :fill="minimapPlan.fill" :paths="minimapPathsPlan"
                  :highlight-id="currentId || hoveredRowId" @select="selectById" @hover="hoveredRowId = $event" />
              </div>
            </div>

            <!-- Notes / comments (folded in from the old details dialog). -->
            <div class="rc-notes">
              <div class="notes-header">
                <span class="notes-title">Notes / Comments</span>
                <span class="notes-count">{{ comments.length }}</span>
              </div>
              <div class="notes-scroll">
                <div v-if="isLoadingComments" class="notes-state">Loading notes…</div>
                <template v-else-if="comments.length">
                  <div v-for="c in comments" :key="c.id" class="note-item">
                    <div class="note-meta">
                      <strong>{{ c.createdBy || 'Unknown' }}</strong>
                      <span>{{ noteTimeAgo(c.createdOn) }}</span>
                    </div>
                    <div class="note-text">{{ c.comment }}</div>
                  </div>
                </template>
                <div v-else class="notes-state">No notes yet.</div>
              </div>
              <div v-if="commentError" class="inline-error">
                <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
                <span>{{ commentError }}</span>
              </div>
              <div v-if="canComment" class="note-entry">
                <v-text-field v-model="newNote" label="Add a note" variant="outlined" density="comfortable"
                  hide-details="auto" placeholder="Type a note…" class="detail-name-field note-input"
                  @keyup.enter="addNote" />
                <button type="button" class="primary-btn note-add-btn" :disabled="!newNote.trim() || isAddingNote"
                  @click="addNote">
                  <v-progress-circular v-if="isAddingNote" indeterminate size="16" width="2" color="#071514" />
                  <span v-else>Add</span>
                </button>
              </div>
            </div>
          </div>
        </template>

        <!-- Caught up / nothing selected -->
        <div v-else class="support-panel rc-empty">
          <template v-if="allCaughtUp">
            <div class="caught-icon"><v-icon size="34" color="#6ee7a0">mdi-check-circle-outline</v-icon></div>
            <p class="caught-title">All caught up</p>
            <p class="caught-sub">Every anomaly in this scope has been reviewed.</p>
            <button type="button" class="primary-btn caught-report" @click="openReportDialog">
              <v-icon size="16">mdi-file-chart-outline</v-icon><span>Generate report</span>
            </button>
          </template>
          <template v-else>
            <v-icon size="34" color="#4a5a60">mdi-gesture-tap</v-icon>
            <p class="caught-sub">Select an anomaly from the list to review it here.</p>
          </template>
        </div>
      </aside>
    </div>
  </div>

    <!-- AI source-image viewer (full-screen overlay): source frame + detection box, plus the parent
         360 panorama when the frame is a cube face. -->
    <ProvenanceImageOverlay v-model="provViewerOpen" :title="provViewer.title"
      :source-url="provViewer.sourceUrl" :source-loading="provViewer.sourceLoading"
      :source-file-name="provViewer.sourceFileName" :bbox="provViewer.bbox" :bbox-label="provViewer.label"
      :reference-id="provViewer.referenceId" :bbox-confidence="provViewer.confidence"
      :is360="provViewer.is360" :raw-box="provViewer.rawBox"
      :equirect-url="provViewer.equirectUrl" :equirect-loading="provViewer.equirectLoading"
      :start-mode="provViewer.startMode" @request-equirect="loadEquirectForViewer" />

    <!-- Review confirmation toast -->
    <v-snackbar v-model="toast.show" :timeout="2400" location="bottom right" :color="toast.color">
      <div class="review-toast">
        <v-icon size="18">{{ toast.icon }}</v-icon>
        <span>{{ toast.text }}</span>
      </div>
    </v-snackbar>

    <!-- Fullscreen minimap -->
    <v-dialog v-model="minimapFull" max-width="1000">
      <div class="mini-full-modal">
        <div class="mini-toolbar">
          <span class="mini-caption">Plot plan</span>
          <button type="button" class="mini-full" title="Close" @click="minimapFull = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="mini-full-stage">
          <AnomalyMinimap title="" axis-label=""
            :points="minimapPlan.points" :fill="minimapPlan.fill" :paths="minimapPathsPlan"
            :highlight-id="currentId || hoveredRowId" @select="selectById" @hover="hoveredRowId = $event" />
        </div>
      </div>
    </v-dialog>

    <!-- Anomaly details / rename dialog -->
    <v-dialog v-model="editDialog" max-width="880" :persistent="!isMobile"
      :transition="isMobile ? 'dialog-bottom-transition' : 'dialog-transition'"
      :content-class="isMobile ? 'anom-detail-sheet' : ''">
      <div class="detail-modal">
        <header class="detail-header">
          <div class="form-eyebrow">Anomaly</div>
          <h2 class="detail-title">{{ editing?.label || 'Anomaly' }}</h2>
        </header>

        <div class="detail-body">
          <!-- Left: image + details / rename -->
          <div class="detail-col detail-col-main">
            <div class="detail-image">
              <img :src="editing?.imageSrc || noImageAvailable" class="detail-image-img"
                alt="Anomaly image" @error="useDefaultImage" />
              <button v-if="editing?.imageSrc" type="button" class="detail-image-dl" title="Download image"
                @click="downloadAnomalyImage(editing)">
                <v-icon size="16">mdi-tray-arrow-down</v-icon>
              </button>
            </div>

            <dl class="detail-meta">
              <!-- Name — pencil to edit inline (same flow as the rail). -->
              <div class="detail-row">
                <dt>Name</dt>
                <dd>
                  <template v-if="canEdit && editingField === 'name'">
                    <span class="rc-inline-row">
                      <input v-model="editName" class="rc-inline-input" placeholder="Anomaly name"
                        @keyup.enter="saveDetails" @keyup.esc="cancelFieldEdit" />
                      <button type="button" class="rc-inline-btn save" :disabled="isSavingName" title="Save" @click="saveDetails">
                        <v-progress-circular v-if="isSavingName" indeterminate size="14" width="2" color="#6ee7a0" />
                        <v-icon v-else size="17">mdi-check</v-icon>
                      </button>
                      <button type="button" class="rc-inline-btn cancel" :disabled="isSavingName" title="Cancel" @click="cancelFieldEdit">
                        <v-icon size="17">mdi-close</v-icon>
                      </button>
                    </span>
                  </template>
                  <span v-else class="rc-ref-value">
                    <span :class="{ muted: !editing?.name }">{{ editing?.name || rowName(editing) }}</span>
                    <button v-if="canEdit" type="button" class="rc-edit-icon" title="Edit name" @click="beginFieldEdit('name')">
                      <v-icon size="14">mdi-pencil-outline</v-icon>
                    </button>
                  </span>
                </dd>
              </div>
              <div class="detail-row">
                <dt>Type</dt>
                <dd>{{ editing?.label || '—' }}</dd>
              </div>
              <div v-if="editing?.assetName" class="detail-row">
                <dt>Asset</dt>
                <dd>{{ editing?.assetName }}</dd>
              </div>
              <div class="detail-row">
                <dt>Scan</dt>
                <dd>{{ editing?.scanName || '—' }}</dd>
              </div>
              <!-- Reference ID — its own property, pencil to edit inline. -->
              <div class="detail-row">
                <dt>Reference ID</dt>
                <dd>
                  <template v-if="canEdit && editingField === 'reference'">
                    <span class="rc-inline-row">
                      <input v-model="editReference" class="rc-inline-input" placeholder="e.g. work order / CMMS ID"
                        @keyup.enter="saveDetails" @keyup.esc="cancelFieldEdit" />
                      <button type="button" class="rc-inline-btn save" :disabled="isSavingName" title="Save" @click="saveDetails">
                        <v-progress-circular v-if="isSavingName" indeterminate size="14" width="2" color="#6ee7a0" />
                        <v-icon v-else size="17">mdi-check</v-icon>
                      </button>
                      <button type="button" class="rc-inline-btn cancel" :disabled="isSavingName" title="Cancel" @click="cancelFieldEdit">
                        <v-icon size="17">mdi-close</v-icon>
                      </button>
                    </span>
                  </template>
                  <span v-else class="rc-ref-value">
                    <span :class="{ muted: !editing?.externalReference }">{{ editing?.externalReference || '—' }}</span>
                    <button v-if="canEdit" type="button" class="rc-edit-icon" title="Edit reference ID" @click="beginFieldEdit('reference')">
                      <v-icon size="14">mdi-pencil-outline</v-icon>
                    </button>
                  </span>
                </dd>
              </div>
              <div class="detail-row">
                <dt>Status</dt>
                <dd>
                  <span class="duallo-status-pill" :class="statusClass(editing?.status)">{{ editing?.status }}</span>
                </dd>
              </div>
            </dl>

            <div v-if="saveError && editingField" class="inline-error">
              <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
              <span>{{ saveError }}</span>
            </div>
          </div>

          <!-- Right: review + notes / comments -->
          <div class="detail-col detail-col-notes">
            <!-- Review: buttons while pending (or re-opened); otherwise a completed summary with the
                 option to change the decision — nothing stays permanently locked. -->
            <div v-if="canReview && isReviewOpen(editing)" class="review-section">
              <div class="review-label">Not yet reviewed — accept or reject this anomaly.</div>
              <div class="review-actions">
                <button type="button" class="review-btn approve" :disabled="isReviewing"
                  @click="reviewAnomaly('Approved')">
                  <v-icon size="16">mdi-check</v-icon>
                  <span>Accept</span>
                </button>
                <button type="button" class="review-btn reject" :disabled="isReviewing"
                  @click="confirmReviewAnomaly('Rejected')">
                  <v-icon size="16">mdi-close</v-icon>
                  <span>Reject</span>
                </button>
              </div>
            </div>
            <div v-else-if="canReview" class="review-section review-done">
              <span class="review-done-text">
                <v-icon size="17" :color="statusIconColor(editing?.status)">{{ statusIcon(editing?.status) }}</v-icon>
                <span>Review complete — <strong>{{ editing?.status }}</strong>.</span>
              </span>
              <button type="button" class="rc-change-btn" @click="reopenReview(editing)">Change decision</button>
            </div>

            <div class="notes-header">
              <span class="notes-title">Notes / Comments</span>
              <span class="notes-count">{{ comments.length }}</span>
            </div>

            <div class="notes-scroll">
              <div v-if="isLoadingComments" class="notes-state">Loading notes…</div>
              <template v-else-if="comments.length">
                <div v-for="c in comments" :key="c.id" class="note-item">
                  <div class="note-meta">
                    <strong>{{ c.createdBy || 'Unknown' }}</strong>
                    <span>{{ noteTimeAgo(c.createdOn) }}</span>
                  </div>
                  <div class="note-text">{{ c.comment }}</div>
                </div>
              </template>
              <div v-else class="notes-state">No notes yet.</div>
            </div>

            <div v-if="commentError" class="inline-error">
              <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
              <span>{{ commentError }}</span>
            </div>

            <div v-if="canComment" class="note-entry">
              <v-text-field v-model="newNote" label="Add a note" variant="outlined" density="comfortable"
                hide-details="auto" placeholder="Type a note…" class="detail-name-field note-input"
                @keyup.enter="addNote" />
              <button type="button" class="primary-btn note-add-btn" :disabled="!newNote.trim() || isAddingNote"
                @click="addNote">
                <v-progress-circular v-if="isAddingNote" indeterminate size="16" width="2" color="#071514" />
                <span v-else>Add</span>
              </button>
            </div>
          </div>
        </div>

        <div class="detail-actions">
          <button type="button" class="text-btn" :disabled="isSavingName" @click="editDialog = false">Close</button>
        </div>
      </div>
    </v-dialog>

    <!-- Generate report dialog -->
    <v-dialog v-model="reportDialog" max-width="440">
      <div class="detail-modal">
        <header class="detail-header">
          <div class="form-eyebrow">Report</div>
          <h2 class="detail-title">Generate findings report</h2>
        </header>

        <p class="report-sub">
          The report is built from the <strong>{{ filteredRows.length }}</strong>
          finding{{ filteredRows.length === 1 ? '' : 's' }} currently shown in the table — it follows your
          active filters (status, type, tags and search). Adjust the filters to change what's included.
        </p>

        <div class="report-label">Include in the report</div>

        <label class="report-check report-extra">
          <input type="checkbox" v-model="reportIncludeImages" />
          <span>Thumbnails</span>
        </label>

        <label v-if="isAssetLocked" class="report-check">
          <input type="checkbox" v-model="reportIncludeMinimap" />
          <span>Plot plan</span>
        </label>

        <label class="report-check">
          <input type="checkbox" v-model="reportIncludeComments" />
          <span>Comments</span>
        </label>

        <div v-if="reportError" class="inline-error">
          <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
          <span>{{ reportError }}</span>
        </div>

        <div class="detail-actions">
          <button type="button" class="text-btn" :disabled="isGeneratingReport"
            @click="reportDialog = false">Cancel</button>
          <button type="button" class="primary-btn" :disabled="!filteredRows.length || isGeneratingReport"
            @click="generateReport">
            <v-progress-circular v-if="isGeneratingReport" indeterminate size="16" width="2" color="#071514" />
            <span v-else>Generate PDF</span>
          </button>
        </div>
      </div>
    </v-dialog>
</template>

<script>
import AssetService from '@/services/AssetService';
import MarkerService from '@/services/MarkerService';
import MarkerProvenanceService from '@/services/MarkerProvenanceService';
import ModelVersionService from '@/services/ModelVersionService';
import DualloFramesClient from '@/clients/duallo/DualloFramesClient';
import SiteService from '@/services/SiteService';
import TagService from '@/services/TagService';
import TagFilterSelect from '@/components/tags/TagFilterSelect.vue';
import { useAssetStore } from '@/store/assetStore';
import { useOrganizationStore } from '@/store/organizationStore';
import { getMarkerStyle, getMarkerStyleById } from '@/lib/markerStyleRegistry';
import organizationSasService from '@/services/OrganizationSasService';
import CreateMarkerCommentModel from '@/domain/CreateMarkerCommentModel';
import { useDefaultImage } from '@/utils/ImageUtils';
import noImageAvailable from '@/assets/no-image-available.svg';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';
import { useConfirm } from '@/composable/useConfirm';
import { useAuth0 } from '@auth0/auth0-vue';
import { useDisplay } from 'vuetify';
import { format } from 'timeago.js';
import * as THREE from 'three';
import { dedupeCubemapHelpers, helperPositions, CAMERA_PATH_COLORS } from '@/lib/viewer/pointCloudCull';
import { deCollideLabels } from '@/lib/viewer/minimapLabels';
import AnomalyMinimap from '@/components/AnomalyMinimap.vue';
import ProvenanceImageOverlay from '@/components/ProvenanceImageOverlay.vue';
import { buildCubeStrip, buildCubeEquirect, cubeFaceLabelOf, cubeBaseOf, faceFovDegOf, fovDegFromHelper } from '@/lib/cubeStitch';

export default {
  name: 'AnomalyManagementView',
  components: { AnomalyMinimap, ProvenanceImageOverlay, TagFilterSelect },
  setup() {
    const { can } = usePermissions();
    const { user } = useAuth0();
    const { confirm } = useConfirm();
    // Breakpoints: <960 = phone/tablet-portrait (list + bottom sheet); 960–1279 = tablet-landscape
    // (list + pop-out drawer); ≥1280 = persistent two-pane.
    const { mdAndUp, smAndDown, lgAndUp } = useDisplay();
    return { can, Permissions, user, confirm, mdAndUp, isMobile: smAndDown, isWideDesktop: lgAndUp };
  },
  data() {
    return {
      assetStore: null,
      organizationStore: null,

      // Mode: 'asset' when reached with an /asset/:id route (locked to that asset), else 'org'.
      isAssetLocked: false,
      lockedAssetId: null,
      lockedAssetName: 'Asset',

      // Org-mode scope data + selections.
      assets: [],           // org's assets (AssetViewModels, carry siteId)
      sites: [],            // org's sites
      selectedSiteId: 'all',
      selectedAssetId: 'all',

      rows: [],
      scanOptions: [],
      noImageAvailable,           // shared placeholder shown when an anomaly has no image
      // AI source-image (provenance) viewer: full-screen overlay showing the exact frame the model
      // ran on, with the detection box drawn, plus the parent 360 panorama when the frame is a cube face.
      provViewerOpen: false,
      provViewerLoading: false,
      provViewer: {
        title: '', sourceUrl: null, sourceFileName: null, bbox: null, label: '', confidence: null,
        is360: false, equirectUrl: null, equirectLoading: false,
        base: null, assetId: null, scanId: null, startMode: 'source',
      },
      // Prefetched provenance for the selected finding, so the media action bar can show accurate
      // enabled/disabled states (does an AI source image exist? is it part of a 360 capture?).
      provMeta: { rowId: null, loading: false, hasProvenance: false, is360: false, record: null },

      // Minimap geometry (asset-locked): per-scan version transform + stored perimeter (parsed).
      scanTransforms: {},         // scanId -> { position, rotation, scale }
      scanPerimeters: {},         // scanId -> { plot:{fill}, elevation:{fill} }
      scanPaths: {},              // scanId -> [{x,y,z}] ordered camera (walking) path, scan-local
      hoveredRowId: null,         // table row under the cursor → enlarge its minimap marker
      expandedId: null,           // the single row whose thumbnail is pinned enlarged (null = none)
      isLoading: true,
      ready: false,               // gates the scope watcher until the initial load is set up
      statusFilter: 'all',        // all | Pending | Approved | Rejected | reviewed
      scanFilter: 'all',
      typeFilter: 'all',
      tagFilter: [],              // multi-select of tag ids; empty = no tag filter
      allTags: [],                // org tags (id/name/color/icon), loaded once; drives the tag filter options
      search: '',
      currentPage: 1,
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100],

      // Review workspace (inline)
      currentId: null,            // the anomaly shown in the review card
      selectedIds: [],            // checkbox selection for bulk review
      reReviewIds: [],            // ids the user re-opened for review after it was accepted/rejected
      detailOpen: false,          // tablet-landscape (960–1279): the review drawer is slid in
      minimapFull: false,

      // Transient confirmation toast after a review action.
      toast: { show: false, text: '', icon: 'mdi-check-circle-outline', color: '#14532d' },

      // Details / rename / review dialog
      editDialog: false,
      editing: null,
      editName: '',
      editReference: '',
      isSavingName: false,
      isReviewing: false,
      saveError: '',
      editingField: null,          // which field is being inline-edited: null | 'name' | 'reference'

      // Notes / comments (right column of the modify dialog)
      comments: [],
      newNote: '',
      isLoadingComments: false,
      isAddingNote: false,
      commentError: '',
      statusFilters: [
        { value: 'Approved', label: 'Approved' },
        { value: 'Rejected', label: 'Rejected' },
        { value: 'Pending', label: 'Pending' },
        { value: 'reviewed', label: 'Reviewed' },
        { value: 'all', label: 'All' },
      ],

      // Report dialog
      reportDialog: false,
      reportError: '',
      reportIncludeImages: true,
      reportIncludeMinimap: true,
      reportIncludeComments: false,
      isGeneratingReport: false,
    };
  },
  computed: {
    activeAssets() {
      return this.assets.filter(a => a.isActive === true || a.isactive === true);
    },
    // Sites that actually have an active asset — no scope option can yield zero assets.
    siteOptions() {
      const assigned = new Set(this.activeAssets.filter(a => a.siteId).map(a => a.siteId));
      return this.sites.filter(s => assigned.has(s.id));
    },
    // Asset dropdown, narrowed by the chosen site.
    assetOptions() {
      const list = this.selectedSiteId === 'all'
        ? this.activeAssets
        : this.activeAssets.filter(a => a.siteId === this.selectedSiteId);
      return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    // The assets whose anomalies are currently in scope.
    scopedAssets() {
      if (this.isAssetLocked) {
        return this.lockedAssetId ? [{ id: this.lockedAssetId, name: this.lockedAssetName, siteTitle: null }] : [];
      }
      let list = this.activeAssets;
      if (this.selectedSiteId !== 'all') list = list.filter(a => a.siteId === this.selectedSiteId);
      if (this.selectedAssetId !== 'all') list = list.filter(a => a.id === this.selectedAssetId);
      return list.map(a => ({ id: a.id, name: a.name, siteTitle: a.siteTitle }));
    },
    // Watch key — ids only, so re-resolving an asset name doesn't trigger a reload.
    scopedAssetIds() {
      return this.scopedAssets.map(a => a.id).join(',');
    },
    singleAssetScope() {
      return this.scopedAssets.length === 1;
    },
    canEdit() {
      return this.can(Permissions.AnomalyUpdate);
    },
    canReview() {
      return this.can(Permissions.AnomalyReview);
    },
    canComment() {
      return this.can(Permissions.AnomalyComment);
    },
    // Absolute URL of the shared no-image placeholder, for use inside the blank report window.
    reportNoImageSrc() {
      try { return new URL(this.noImageAvailable, window.location.origin).href; } catch { return this.noImageAvailable; }
    },
    // The asset whose spatial geometry feeds the minimap: the locked asset, or the single asset in
    // scope when browsing the org view (so picking one asset reveals its plot plan).
    minimapAssetId() {
      if (this.isAssetLocked) return this.lockedAssetId;
      return (this.singleAssetScope && this.scopedAssets.length === 1) ? this.scopedAssets[0].id : null;
    },
    // Minimap shows whenever a single asset is in scope, on desktop/tablet (not phones).
    showMinimap() {
      return !!this.minimapAssetId && this.mdAndUp;
    },
    hasStoredPerimeter() {
      return Object.keys(this.scanPerimeters || {}).length > 0;
    },
    // Per-scan world matrix (translate * rotate(euler) * scale), matching ViewerEngine.applyTransform.
    scanMatrices() {
      const out = {};
      for (const [scanId, t] of Object.entries(this.scanTransforms || {})) {
        const p = t?.position ?? {}, r = t?.rotation ?? {}, s = t?.scale ?? {};
        const pos = new THREE.Vector3(p.x ?? 0, p.y ?? 0, p.z ?? 0);
        const quat = new THREE.Quaternion().setFromEuler(new THREE.Euler(r.x ?? 0, r.y ?? 0, r.z ?? 0, r.order || 'XYZ'));
        const scl = new THREE.Vector3(s.x ?? 1, s.y ?? 1, s.z ?? 1);
        out[scanId] = new THREE.Matrix4().compose(pos, quat, scl);
      }
      return out;
    },
    // Anomalies plotted on the minimap = the current table view (filtered + searched), numbered 1..N
    // in a stable order. Positions are transformed to the version's world frame (fixes multi-scan).
    minimapMarkers() {
      const mapped = this.filteredRows.map((r, i) => {
        let x = r.x, y = r.y, z = r.z;
        if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
          const w = this.toWorld(r.scanId, x, y, z);
          x = w.x; y = w.y; z = w.z;
        }
        return { id: r.id, number: i + 1, color: this.statusDotColor(r.status), label: r.label, imageSrc: r.imageSrc, hasImage: !!r.thumbnailPath, x, y, z };
      });
      // Height band (Low / Medium / High) from each marker's world Z, judged against the mean of all
      // plotted markers (± half a standard deviation). Replaces the removed side-elevation minimap —
      // shown in the marker's hover tooltip on the plot plan.
      const zs = mapped.map(m => m.z).filter(Number.isFinite);
      const mean = zs.length ? zs.reduce((a, b) => a + b, 0) / zs.length : 0;
      const variance = zs.length ? zs.reduce((a, b) => a + (b - mean) ** 2, 0) / zs.length : 0;
      const std = Math.sqrt(variance);
      for (const m of mapped) {
        m.elevation = Number.isFinite(m.z) ? this.elevationBand(m.z, mean, std) : null;
      }
      return mapped;
    },
    // Plot plan = top-down horizontal plane (X across, Y depth) — Z is up.
    minimapPlan() {
      const pts = this.minimapMarkers
        .filter(m => Number.isFinite(m.x) && Number.isFinite(m.y))
        .map(m => ({ ...m, u: m.x, v: m.y }));
      const combined = this.combinedPerimeter('plot');
      return { points: pts, fill: combined ? combined.fill : [] };
    },
    // Camera walking path(s) transformed to the version world frame, one ordered polyline per scan,
    // projected top-down (X/Y) for the plot plan.
    minimapPathsPlan() {
      return this.projectPaths('plot');
    },
    // The rail edits the inline detail (editing), which syncInlineDetail keeps pointed at currentRow.
    // Gate the rail's pencils on that alignment so they never act on a stale row.
    canEditThisRow() {
      return this.canEdit && !!this.editing && !!this.currentRow && this.editing.id === this.currentRow.id;
    },
    headingTitle() {
      if (this.isAssetLocked) return this.lockedAssetName;
      if (this.selectedAssetId !== 'all') {
        return this.assetOptions.find(a => a.id === this.selectedAssetId)?.name ?? 'Asset';
      }
      if (this.selectedSiteId !== 'all') {
        return this.siteOptions.find(s => s.id === this.selectedSiteId)?.title ?? 'Site';
      }
      return 'All assets';
    },
    headingSub() {
      return this.isAssetLocked
        ? 'Reviewed anomalies across every scan of this asset, and what was done with them.'
        : 'Select a site or asset to focus. Showing reviewed anomalies across the current scope.';
    },
    // Rows matching the scan filter + search, but NOT the status filter. The status counts and the
    // table both derive from this, so the counts reflect the current search and stay in sync.
    searchedRows() {
      const q = this.search.trim().toLowerCase();
      return this.rows.filter(r => {
        if (this.singleAssetScope && this.scanFilter !== 'all' && r.scanId !== this.scanFilter) return false;
        if (this.typeFilter !== 'all' && (r.label || 'Anomaly') !== this.typeFilter) return false;
        // Tag filter: show a row if it carries ANY of the selected tags (OR match).
        if (this.tagFilter.length
          && !(Array.isArray(r.tagIds) && r.tagIds.some(id => this.tagFilter.includes(id)))) return false;
        if (q
          && !(r.name || '').toLowerCase().includes(q)
          && !(r.externalReference || '').toLowerCase().includes(q)
          && !(r.label || '').toLowerCase().includes(q)
          && !(r.scanName || '').toLowerCase().includes(q)
          && !(r.assetName || '').toLowerCase().includes(q)) {
          return false;
        }
        return true;
      });
    },
    // Count per status across the searched set — no extra API calls, just tallied from loaded rows.
    statusCounts() {
      let approved = 0, rejected = 0, pending = 0;
      for (const r of this.searchedRows) {
        const s = (r.status || '').toLowerCase();
        if (s === 'approved' || s === 'accepted') approved++;
        else if (s === 'rejected') rejected++;
        else pending++;
      }
      return {
        Approved: approved,
        Rejected: rejected,
        Pending: pending,
        reviewed: approved + rejected,
        all: this.searchedRows.length,
      };
    },
    filteredRows() {
      const base = this.searchedRows;
      if (this.statusFilter === 'all') return base;
      if (this.statusFilter === 'reviewed') return base.filter(r => !this.isPending(r.status));
      if (this.statusFilter === 'Pending') return base.filter(r => this.isPending(r.status));
      return base.filter(r => r.status === this.statusFilter);   // Approved / Rejected
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    },
    // The rows for the current page. Sorting/filtering/merging happen on the full set (above); this
    // just slices the visible window.
    pagedRows() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    },
    pageStart() {
      return this.filteredRows.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    },
    pageEnd() {
      return Math.min(this.currentPage * this.pageSize, this.filteredRows.length);
    },

    // ----- Review workspace -----
    // Status tabs shown in the filter bar (order: All, Pending, Approved, Rejected).
    tabFilters() {
      return [
        { value: 'all', label: 'All' },
        { value: 'Pending', label: 'Pending' },
        { value: 'Approved', label: 'Approved' },
        { value: 'Rejected', label: 'Rejected' },
      ];
    },
    // Distinct anomaly types across the loaded rows, for the "All types" filter.
    typeOptions() {
      const set = new Set();
      for (const r of this.rows) set.add(r.label || 'Anomaly');
      return [...set].sort((a, b) => a.localeCompare(b));
    },
    // Tags actually assigned to at least one loaded row, resolved to tag metadata. Only these are
    // offered in the filter, so you never see a tag that nothing in the current scope uses.
    tagOptions() {
      const present = new Set();
      for (const r of this.rows) (r.tagIds || []).forEach(id => present.add(id));
      return this.allTags.filter(t => present.has(t.id));
    },
    // The right-rail review card only shows on wide viewports.
    showRail() {
      return this.mdAndUp;
    },
    // "45 found across all scans of …" vs "… this scan …" when a single scan is filtered.
    scanScopeLabel() {
      if (this.singleAssetScope && this.scanFilter !== 'all') {
        const s = this.scanOptions.find(o => o.id === this.scanFilter);
        return s ? `scan “${s.name}”` : 'this scan';
      }
      return 'all scans';
    },
    currentRow() {
      return this.filteredRows.find(r => r.id === this.currentId) || null;
    },
    currentIndex() {
      return this.filteredRows.findIndex(r => r.id === this.currentId);
    },
    allVisibleSelected() {
      return this.filteredRows.length > 0 && this.filteredRows.every(r => this.selectedIds.includes(r.id));
    },
    // Pending tab selected but nothing left pending (and anomalies do exist) → "all caught up".
    allCaughtUp() {
      return this.statusFilter === 'Pending' && this.filteredRows.length === 0 && this.statusCounts.all > 0;
    },
  },
  watch: {
    // Reset the asset choice when the site changes (its asset list changes).
    selectedSiteId() {
      this.selectedAssetId = 'all';
    },
    // Filters and page-size changes send the user back to the first page.
    statusFilter() { this.currentPage = 1; this.selectedIds = []; },
    scanFilter() { this.currentPage = 1; this.selectedIds = []; },
    typeFilter() { this.currentPage = 1; this.selectedIds = []; },
    tagFilter() { this.currentPage = 1; this.selectedIds = []; },
    search() { this.currentPage = 1; },
    pageSize() { this.currentPage = 1; },
    // If the filtered set shrinks past the current page, snap back into range.
    totalPages(pages) {
      if (this.currentPage > pages) this.currentPage = pages;
    },
    // Keep the review card's "current" pointer valid as the filtered set changes; default to the
    // first row so the card is populated (matches the mockup's "1 OF N").
    filteredRows(rows) {
      if (!rows.length) { this.currentId = null; return; }
      if (!rows.some(r => r.id === this.currentId)) this.currentId = rows[0].id;
    },
    currentId() {
      this.syncInlineDetail();
      this.loadProvMeta();   // prefetch AI provenance so the media bar's Source image / 360 states are accurate
      this.$nextTick(() => this.scrollCurrentIntoView());
    },
    // If the layout crosses into the rail breakpoint, make sure the inline detail is populated.
    showRail() {
      this.syncInlineDetail();
    },
    // Any change to the scoped set reloads the anomalies. Reset the scan filter since scan
    // ids are only meaningful within a single asset. Gated until the initial load is ready
    // so the setup in created()/mounted() doesn't trigger a duplicate fetch.
    scopedAssetIds() {
      if (!this.ready) return;
      this.scanFilter = 'all';
      this.loadForScope();
      // A single asset in scope (locked or picked in the org view) gets its plot-plan geometry.
      this.loadMinimapGeometry();
    },
  },
  created() {
    // Non-reactive guard for out-of-order comment loads (see loadComments).
    this._commentsToken = 0;
    // Set the mode synchronously so the first scope resolution is correct.
    const routeId = this.$route.params.id;
    this.isAssetLocked = !!routeId;
    this.lockedAssetId = routeId || null;
  },
  async mounted() {
    this.assetStore = useAssetStore();
    this.organizationStore = useOrganizationStore();
    this.loadTags();   // org tags for the tag filter (non-blocking — the list load doesn't wait on it)

    if (this.isAssetLocked) {
      await this.resolveLockedAsset();
    } else {
      await this.loadOrgScopeData();
    }
    // Run the initial load exactly once, then let the scope watcher handle user changes.
    this.ready = true;
    await this.loadForScope();

    // Default to the "All" tab (statusFilter starts as 'all') so the full list shows on open.

    // Seed the review card with the first anomaly.
    if (!this.currentId && this.filteredRows.length) this.currentId = this.filteredRows[0].id;
    // Populate the inline detail surface (desktop/tablet) for the seeded selection.
    this.syncInlineDetail();

    // Opened from the asset card's "Generate report" button → open the report dialog straight away.
    if (this.$route.query.report) this.openReportDialog();

    // Asset-locked minimap: pull the version transforms + stored per-scan perimeters (non-blocking).
    if (this.isAssetLocked) this.loadMinimapGeometry();
  },
  methods: {
    useDefaultImage,   // expose the imported <img> @error fallback to the template
    // Download the anomaly's shown image (its thumbnail) via the already-signed URL. Fetch → blob so
    // the browser saves it with a sensible name; if blob CORS blocks the fetch, fall back to opening
    // the signed URL in a new tab for a manual save.
    async downloadAnomalyImage(row) {
      const target = row || this.currentRow || this.editing;
      const url = target?.imageSrc;
      if (!url) return;
      const base = (target?.label || target?.name || 'anomaly')
        .replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '') || 'anomaly';
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const ext = blob.type && blob.type.split('/')[1] ? blob.type.split('/')[1].split('+')[0] : 'webp';
        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = `${base}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
      } catch {
        window.open(url, '_blank', 'noopener');
      }
    },

    // Prefetch the selected finding's provenance (AI markers only) so the media action bar can enable
    // or disable Source image / 360 accurately. Cheap: matched by the durable marker id (fallback
    // nearest-position), cached per row. Null-safe when the row isn't AI-generated.
    async loadProvMeta() {
      const row = this.currentRow;
      const empty = { rowId: row?.id ?? null, loading: false, hasProvenance: false, is360: false, record: null };
      // Any marker can have provenance now (AI detections and manually-drawn street-view markers),
      // so gate the media buttons on whether a record exists, not on isAIGenerated.
      if (!row) { this.provMeta = empty; return; }
      if (this.provMeta.rowId === row.id && (this.provMeta.record || this.provMeta.loading)) return;   // already have it

      const rowId = row.id;
      this.provMeta = { ...empty, loading: true };
      try {
        const shim = { id: row.id, transform: { position: { x: row.x, y: row.y, z: row.z } } };
        const match = await MarkerProvenanceService.getForMarker(row.scanId, shim);
        if (this.currentId !== rowId) return;   // selection moved on while fetching
        const rec = match?.canonical || null;
        this.provMeta = {
          rowId,
          loading: false,
          hasProvenance: !!rec,
          is360: !!rec && /_cube_/i.test(rec.sourceImageFileName || ''),
          record: rec,
        };
      } catch {
        if (this.currentId === rowId) this.provMeta = empty;
      }
    },

    // Open the AI source-image viewer for a finding: the exact frame the model ran on, with the
    // detection box drawn. `startMode` 'equirect' opens straight on the parent 360 panorama (a frame
    // named "<base>_cube_<face>" is one face of a 360 capture). Reuses the prefetched record.
    async openProvenanceImage(row, startMode = 'source') {
      if (!row || this.provViewerLoading) return;
      this.provViewerLoading = true;
      try {
        let rec = (this.provMeta.rowId === row.id && this.provMeta.record) ? this.provMeta.record : null;
        if (!rec) {
          const shim = { id: row.id, transform: { position: { x: row.x, y: row.y, z: row.z } } };
          const match = await MarkerProvenanceService.getForMarker(row.scanId, shim);
          rec = match?.canonical || null;
        }
        if (!rec) {
          this.showReviewToast('No AI source image recorded for this finding.', 'error');
          return;
        }
        // A legacy multi-face capture names its source by cube face (..._left.png) and has no stored
        // equirect; modern equirect findings reference the panorama file itself. The former is stitched
        // into one full panorama below; the latter keeps the existing street-view/equirect tab.
        const faceLabel = cubeFaceLabelOf(rec.sourceImageFileName);
        const isLegacyCube = !!faceLabel;
        const is360 = !isLegacyCube && /_cube_/i.test(rec.sourceImageFileName || '');
        const r = rec.bboxRect;
        // Open immediately (shimmer), resolve the image in the background so it feels instant.
        this.provViewer = {
          title: this.rowName(row) || row.label || 'Anomaly',
          label: rec.label || row.label || 'Marker',
          referenceId: row.externalReference || null,
          confidence: rec.confidence ?? null,
          sourceUrl: null,
          sourceLoading: true,
          sourceFileName: rec.sourceImageFileName,
          bbox: { left: r.left, top: r.top, width: r.width, height: r.height },
          rawBox: false,
          is360,
          equirectUrl: null,
          equirectLoading: false,
          base: is360 ? (rec.sourceImageFileName.split(/_cube_/i)[0]) : null,
          assetId: row.assetId,
          scanId: row.scanId,
          startMode: (startMode === 'equirect' && is360) ? 'equirect' : 'source',
        };
        this.provViewerOpen = true;
        const srcKey = rec.sourceImageFileName;

        if (isLegacyCube) {
          // Stitch the rig's faces into one panorama (equirect from 6 faces, else a 360° side strip) so
          // the full-size view shows the whole detection in context, not a single face.
          const pano = await this.buildLegacyPanorama(rec, faceLabel, row.scanId).catch(() => null);
          if (!this.provViewerOpen || this.provViewer?.sourceFileName !== srcKey) return;
          if (pano) {
            this.provViewer = { ...this.provViewer, sourceUrl: pano.url, bbox: pano.box || this.provViewer.bbox, rawBox: true, sourceFileName: pano.base, sourceLoading: false };
          } else {
            const sourceUrl = await MarkerProvenanceService.getSourceImageUrl(rec, {
              scanId: row.scanId, assetId: row.assetId, orgId: this.organizationStore?.organizationId,
            }).catch(() => null);
            if (this.provViewerOpen && this.provViewer?.sourceFileName === srcKey) this.provViewer = { ...this.provViewer, sourceUrl, sourceLoading: false };
          }
          return;
        }

        const sourceUrl = await MarkerProvenanceService.getSourceImageUrl(rec, {
          scanId: row.scanId, assetId: row.assetId, orgId: this.organizationStore?.organizationId,
        }).catch(() => null);
        if (this.provViewerOpen && this.provViewer?.sourceFileName === rec.sourceImageFileName) {
          this.provViewer = { ...this.provViewer, sourceUrl, sourceLoading: false };
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to open provenance image', e);
        this.showReviewToast('Could not load the source image.', 'error');
      } finally {
        this.provViewerLoading = false;
      }
    },

    // Stitch a legacy capture's faces into a panorama and map the detection's single-face box onto it:
    // an on-the-fly equirect when all 6 faces exist, otherwise a 360° side strip. Returns { url, box,
    // base } or null (caller then falls back to the single source face).
    async buildLegacyPanorama(rec, faceLabel, scanId) {
      const base = cubeBaseOf(rec.sourceImageFileName);
      const [frames, helpersByStem] = await Promise.all([
        MarkerProvenanceService.getScanFrames(scanId),
        MarkerProvenanceService.getCameraHelpersByStem(scanId).catch(() => ({})),
      ]);
      const faces = (frames || [])
        .filter(f => cubeBaseOf(f.fileName) === base && cubeFaceLabelOf(f.fileName))
        // FOV from the camera-helper intrinsics first (filename-independent), falling back to the _fNN_ stamp.
        .map(f => ({ label: cubeFaceLabelOf(f.fileName), url: f.url, fovDeg: fovDegFromHelper(helpersByStem[f.stem]) ?? faceFovDegOf(f.fileName) }));
      const r = rec.bboxRect;
      const faceBox = { left: r.left, top: r.top, width: r.width, height: r.height };
      const has = (l) => faces.some(f => f.label === l);

      if (has('up') && has('down')) {
        const eq = await buildCubeEquirect(faces).catch(() => null);
        const box = eq && eq.mapBox(faceLabel, faceBox);
        if (eq && box) return { url: eq.dataUrl, box, base };
      }
      const strip = await buildCubeStrip(faces);
      const box = strip && strip.mapBox(faceLabel, faceBox);
      if (strip && box) return { url: strip.dataUrl, box, base };
      return null;
    },
    // Lazy-load the parent 360 panorama for the current viewer (on first switch to the panorama tab).
    async loadEquirectForViewer() {
      const v = this.provViewer;
      if (!v.base || v.equirectUrl || v.equirectLoading) return;
      v.equirectLoading = true;
      try {
        const orgId = useOrganizationStore().organizationId;
        const paths = await DualloFramesClient.getEquirectFramePaths(orgId, v.assetId, v.scanId);
        const match = (paths || []).find(p => this.equirectBaseOf(p) === v.base);
        v.equirectUrl = match
          ? await organizationSasService.getSignedUrl(match.startsWith('/') ? match : `/${match}`)
          : null;
      } catch {
        v.equirectUrl = null;
      } finally {
        v.equirectLoading = false;
      }
    },

    // Capture base of an equirect blob path: filename stem minus the modality suffix (_equirect/_cube/…).
    equirectBaseOf(path) {
      const stem = (path || '').split(/[\\/]/).pop().replace(/\.[^.]+$/, '');
      return stem.replace(/[_-](equirect|cube|pano|360)$/i, '');
    },
    // Display identity for an unnamed anomaly, so rows aren't anonymous "—". Type + scan (when
    // meaningful) + a short id suffix to disambiguate. Never persisted — the Name field stays empty
    // until the user sets one.
    rowTitle(row) {
      const parts = [row.label || 'Anomaly'];
      if (row.scanName && row.scanName !== 'Unknown scan') parts.push(row.scanName);
      if (row.id) parts.push('#' + row.id.slice(0, 6));
      return parts.join(' · ');
    },
    // Convex hull (monotone chain) of [[u,v],…] → hull [[u,v],…].
    hull2d(points) {
      const pts = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
      if (pts.length < 3) return pts;
      const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
      const lower = [];
      for (const p of pts) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
        lower.push(p);
      }
      const upper = [];
      for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
        upper.push(p);
      }
      lower.pop();
      upper.pop();
      return lower.concat(upper);
    },
    convexHull2d(pts) {
      return this.hull2d(pts.map(p => [p.u, p.v]));
    },
    // Load the version's per-scan transforms + stored perimeters for the minimap (best-effort).
    async loadMinimapGeometry() {
      const assetId = this.minimapAssetId;
      if (!assetId) { this.scanTransforms = {}; this.scanPerimeters = {}; this.scanPaths = {}; return; }
      try {
        const [version, scans] = await Promise.all([
          ModelVersionService.getCurrentModelVersion(assetId).catch(() => null),
          ModelVersionService.getScansByAssetId(assetId).catch(() => []),
        ]);
        const transforms = {};
        for (const s of (version?.scans || [])) if (s?.scanId) transforms[s.scanId] = s.transform;
        const perims = {};
        for (const s of (scans || [])) {
          if (s?.perimeter) {
            try { perims[s.id] = JSON.parse(s.perimeter); } catch { /* ignore malformed perimeter */ }
          }
        }
        this.scanTransforms = transforms;
        this.scanPerimeters = perims;

        // Camera walking paths (deduped + ordered), same as the 3D viewer builds them.
        const scanIds = (scans || []).map(s => s.id).filter(Boolean);
        const paths = {};
        await Promise.all(scanIds.map(async (scanId) => {
          try {
            const raw = await AssetService.getCameraHelpersAsync(scanId);
            // Order the stations by capture sequence (imageName, numeric) before connecting them —
            // otherwise the route zig-zags in raw API order. Matches ModelViewer.attachScanCameraHelpers.
            const ordered = dedupeCubemapHelpers(raw || [])
              .sort((a, b) => (a.imageName ?? '').localeCompare(b.imageName ?? '', undefined, { numeric: true }));
            paths[scanId] = helperPositions(ordered);
          } catch { paths[scanId] = []; }
        }));
        this.scanPaths = paths;
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.warn('Minimap geometry load failed', e);
      }
    },
    // World-space transform for a marker/perimeter point in its scan's local coords → { x, y, z }.
    // Matches ViewerEngine.applyTransform (translate * rotate(euler) * scale).
    toWorld(scanId, x, y, z) {
      const m = this.scanMatrices[scanId];
      const v = new THREE.Vector3(x, y, z);
      if (m) v.applyMatrix4(m);
      return v;
    },
    downsamplePairs(arr, cap) {
      if (arr.length <= cap) return arr;
      const stride = arr.length / cap;
      const out = [];
      for (let i = 0; i < cap; i++) out.push(arr[Math.floor(i * stride)]);
      return out;
    },
    // Combine every scan's stored footprint slice for a view ('plot' | 'elevation'): transform each
    // point to the version world frame and project to 2D. Returns null when no scan has a stored
    // footprint yet (the minimap then just shows the anomaly markers, no boundary).
    combinedPerimeter(view) {
      const perims = this.scanPerimeters || {};
      const ids = Object.keys(perims);
      if (!ids.length) return null;
      const key = view === 'plot' ? 'plot' : 'elevation';
      // Z-up: plan is the horizontal X/Y plane; elevation is X across, Z up (height).
      const proj = view === 'plot' ? (v) => [v.x, v.y] : (v) => [v.x, v.z];
      const fillWorld = [];
      const tmp = new THREE.Vector3();
      for (const id of ids) {
        const per = perims[id]?.[key];
        if (!per) continue;
        const m = this.scanMatrices[id];
        for (const p of (per.fill || [])) {
          tmp.set(p[0], p[1], p[2]);
          if (m) tmp.applyMatrix4(m);
          fillWorld.push(proj(tmp));
        }
      }
      if (!fillWorld.length) return null;
      return { fill: this.downsamplePairs(fillWorld, 2500) };
    },
    // Minimap dot colour by review status — green approved, red rejected, orange pending.
    statusDotColor(status) {
      const s = (status || '').toLowerCase();
      if (s === 'approved' || s === 'accepted') return '#4ade80';
      if (s === 'rejected') return '#f87171';
      return '#f59e0b';
    },
    // Height band label from a marker's world Z, relative to the mean of all plotted markers. A tight
    // spread (≈0 std) reads as "Medium" for everything rather than splitting hairs on noise.
    elevationBand(z, mean, std) {
      if (!(std > 1e-6)) return 'Medium';
      if (z > mean + std * 0.5) return 'High';
      if (z < mean - std * 0.5) return 'Low';
      return 'Medium';
    },
    // Icon + colour for a completed review's status pill in the rail / mobile "review complete" note.
    statusIcon(status) {
      const s = (status || '').toLowerCase();
      if (s === 'approved' || s === 'accepted') return 'mdi-check-circle-outline';
      if (s === 'rejected') return 'mdi-close-circle-outline';
      return 'mdi-progress-clock';
    },
    statusIconColor(status) {
      const s = (status || '').toLowerCase();
      if (s === 'approved' || s === 'accepted') return '#4ade80';
      if (s === 'rejected') return '#f87171';
      return '#fbbf24';
    },
    // Whether the review buttons should show for a row: still pending, or the user re-opened it.
    isReviewOpen(row) {
      return !!row && this.canReview && (this.isPending(row.status) || this.reReviewIds.includes(row.id));
    },
    // Re-open an already-reviewed finding so its decision can be changed (nothing is permanently locked).
    reopenReview(row) {
      if (row && !this.reReviewIds.includes(row.id)) this.reReviewIds.push(row.id);
    },
    // Transient toast after a review action. status: 'Approved' | 'Rejected' | 'error'.
    showReviewToast(text, status) {
      const s = (status || '').toLowerCase();
      const icon = s === 'error' ? 'mdi-alert-circle-outline'
        : s === 'rejected' ? 'mdi-close-circle-outline' : 'mdi-check-circle-outline';
      const color = s === 'error' ? '#7a1f1f' : s === 'rejected' ? '#7f1d1d' : '#14532d';
      this.toast = { show: true, text, icon, color };
    },
    // Ordered camera path(s) per scan → world frame → projected. plot: X/Y, elevation: X/Z. Each scan
    // gets a distinct colour (from the shared camera-path palette) so multi-scan routes are tellable apart.
    projectPaths(view) {
      const out = [];
      const tmp = new THREE.Vector3();
      const entries = Object.entries(this.scanPaths || {});
      for (let idx = 0; idx < entries.length; idx++) {
        const [scanId, pts] = entries[idx];
        if (!pts || pts.length < 2) continue;
        const m = this.scanMatrices[scanId];
        const line = pts.map((p) => {
          tmp.set(p.x, p.y, p.z);
          if (m) tmp.applyMatrix4(m);
          return view === 'plot' ? [tmp.x, tmp.y] : [tmp.x, tmp.z];
        });
        out.push({ points: line, color: CAMERA_PATH_COLORS[idx % CAMERA_PATH_COLORS.length] });
      }
      return out;
    },
    // ----- Review workspace -----
    shortId(id) {
      return String(id || '').slice(0, 6);
    },
    // Row title = the custom name, else just the type. Scan + id live on the second line only.
    rowName(row) {
      return row.name || row.label || 'Anomaly';
    },
    // Type dots get their own palette (blues/purples/teal/greys) so they never collide with the
    // status colours (green approved / red rejected / amber pending).
    typeColor(label) {
      const palette = ['#7aa2ff', '#c792ea', '#64ffda', '#5eb0d6', '#b48ead', '#4db6ac', '#9aa7b0', '#8e7cff'];
      const s = String(label || 'Anomaly');
      let h = 0;
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
      return palette[h % palette.length];
    },
    selectRow(row) {
      this.currentId = row.id;
      // Tablet-landscape: selecting a row pops the review drawer open (persistent pane needs no toggle).
      if (this.showRail && !this.isWideDesktop) this.detailOpen = true;
    },
    // Dismiss the tablet-landscape review drawer.
    closeDetail() {
      this.detailOpen = false;
    },
    // Minimap marker click → select into the review card. If the marker isn't in the current filtered
    // set, widen to "All" so it can be shown inline (rather than popping a separate dialog).
    selectById(id) {
      if (this.filteredRows.some(r => r.id === id)) { this.currentId = id; return; }
      if (this.rows.some(r => r.id === id)) {
        this.statusFilter = 'all';
        this.$nextTick(() => { this.currentId = id; });
      }
    },
    scrollCurrentIntoView() {
      const el = this.$refs.listScroll;
      if (!el || !this.currentId) return;
      const row = el.querySelector(`[data-id="${this.currentId}"]`);
      if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
    },
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    toggleSelect(id) {
      const i = this.selectedIds.indexOf(id);
      if (i === -1) this.selectedIds.push(id);
      else this.selectedIds.splice(i, 1);
    },
    toggleSelectAll() {
      this.selectedIds = this.allVisibleSelected ? [] : this.filteredRows.map(r => r.id);
    },
    clearSelection() {
      this.selectedIds = [];
    },
    async bulkReview(status) {
      const rows = this.filteredRows.filter(r => this.selectedIds.includes(r.id));
      if (!rows.length) return;
      // Rejecting is destructive to the review record, so confirm it (bulk or single).
      if (status === 'Rejected') {
        const ok = await this.confirm({
          title: `Reject ${rows.length} finding${rows.length > 1 ? 's' : ''}?`,
          message: 'This marks the selected findings as rejected.',
          confirmText: 'Reject', cancelText: 'Cancel', danger: true, icon: 'mdi-close-circle-outline',
        });
        if (!ok) return;
      }
      this.isReviewing = true;
      let ok = 0, fail = 0;
      try {
        for (const row of rows) {
          if (await this.reviewRow(row, status, true)) ok++; else fail++;
        }
      } finally {
        this.isReviewing = false;
        this.selectedIds = [];
      }
      // Report what actually happened — never claim success for rows the server rejected.
      const verb = status === 'Rejected' ? 'rejected' : 'approved';
      if (fail && ok) this.showReviewToast(`${ok} ${verb}, ${fail} couldn’t be updated — please retry`, 'error');
      else if (fail) this.showReviewToast(`Couldn’t update ${fail} finding${fail > 1 ? 's' : ''} — please retry`, 'error');
      else this.showReviewToast(`${ok} finding${ok > 1 ? 's' : ''} ${verb}`, status);
    },
    // Review a specific row (used by the inline card + bulk actions). Returns true on success, false on
    // failure. `silent` skips the per-call busy state + toast so a batch caller can manage it once.
    async reviewRow(row, status, silent = false) {
      if (!row) return false;
      if (!silent) { this.isReviewing = true; this.saveError = ''; }
      try {
        const updated = await AssetService.reviewAnomaly(row.scanId, row.id, status);
        row.status = updated ? updated.status : status;
        // A fresh decision closes the "re-review" state so the buttons collapse back to the summary.
        this.reReviewIds = this.reReviewIds.filter(id => id !== row.id);
        if (!silent) this.showReviewToast(`“${this.rowName(row)}” ${(status || '').toLowerCase() === 'rejected' ? 'rejected' : 'approved'}`, status);
        return true;
      } catch (e) {
        if (!silent) {
          this.saveError = this.actionError(e, 'Could not update the review status. Please try again.');
          this.showReviewToast(`Couldn’t update “${this.rowName(row)}” — please try again`, 'error');
        }
        return false;
      } finally {
        if (!silent) this.isReviewing = false;
      }
    },
    // Reject goes through the shared confirm service (same flow on desktop + mobile), then advances.
    async requestReject() {
      if (!this.currentRow || !this.canReview) return;
      const ok = await this.confirm({
        title: 'Reject finding?',
        message: `Mark "${this.rowName(this.currentRow)}" as rejected and move to the next finding.`,
        confirmText: 'Reject',
        cancelText: 'Cancel',
        danger: true,
        icon: 'mdi-close-circle-outline',
      });
      if (!ok) return;
      await this.rejectCurrentNext();
    },
    // Reject the current anomaly, then advance to the next one (mirrors approveCurrentNext).
    async rejectCurrentNext() {
      const cur = this.currentRow;
      if (!cur) return;
      const idx = this.filteredRows.findIndex(r => r.id === cur.id);
      if (!await this.reviewRow(cur, 'Rejected')) return;   // stay put on failure (error toast shown)
      const list = this.filteredRows;
      if (!list.length) { this.currentId = null; return; }
      const stillThere = list.some(r => r.id === cur.id);
      let next = stillThere ? idx + 1 : idx;
      if (next >= list.length) next = list.length - 1;
      if (next < 0) next = 0;
      this.currentId = list[next].id;
    },
    // Approve goes through the shared confirm service (same flow on desktop + mobile), then advances.
    async requestApprove() {
      if (!this.currentRow || !this.canReview) return;
      const ok = await this.confirm({
        title: 'Approve finding?',
        message: `Mark "${this.rowName(this.currentRow)}" as approved and move to the next finding.`,
        confirmText: 'Approve',
        cancelText: 'Cancel',
        icon: 'mdi-check-circle-outline',
      });
      if (!ok) return;
      await this.approveCurrentNext();
    },
    // Approve the current anomaly, then advance to the next one (works whether or not it stays in the
    // filtered set — e.g. under the Pending filter the approved row drops out).
    async approveCurrentNext() {
      const cur = this.currentRow;
      if (!cur) return;
      const idx = this.filteredRows.findIndex(r => r.id === cur.id);
      if (!await this.reviewRow(cur, 'Approved')) return;   // stay put on failure (error toast shown)
      const list = this.filteredRows;
      if (!list.length) { this.currentId = null; return; }
      const stillThere = list.some(r => r.id === cur.id);
      let next = stillThere ? idx + 1 : idx;
      if (next >= list.length) next = list.length - 1;
      if (next < 0) next = 0;
      this.currentId = list[next].id;
    },
    currentNext() {
      const list = this.filteredRows;
      if (!list.length) return;
      this.currentId = list[Math.min(this.currentIndex + 1, list.length - 1)].id;
    },
    currentPrev() {
      const list = this.filteredRows;
      if (!list.length) return;
      this.currentId = list[Math.max(this.currentIndex - 1, 0)].id;
    },
    openIn3D(row) {
      if (!row) return;
      this.$router.push({ name: 'AssetViewer', params: { id: row.assetId }, query: { marker: row.id } });
    },
    isExpanded(id) {
      return this.expandedId === id;
    },
    // Click pins a thumbnail enlarged; clicking another pins that one (single selection), and
    // clicking the pinned one again shrinks it. Hover-preview still works on all the others.
    toggleExpand(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    goToPage(page) {
      this.currentPage = Math.min(Math.max(1, page), this.totalPages);
      this.expandedId = null;   // the pinned row may not be on the new page
    },
    openEdit(row) {
      this.editing = row;
      this.editName = row.name ?? '';
      this.editReference = row.externalReference ?? '';
      this.saveError = '';
      this.editingField = null;
      this.newNote = '';
      this.commentError = '';
      this.editDialog = true;
      this.loadComments(row.id);
    },
    // Start editing a single field (name | reference) via its pencil; seed its draft from the persisted value.
    beginFieldEdit(field) {
      if (!this.editing) return;
      this.editName = this.editing.name ?? '';
      this.editReference = this.editing.externalReference ?? '';
      this.saveError = '';
      this.editingField = field;
    },
    // Discard the in-progress field edit.
    cancelFieldEdit() {
      this.editName = this.editing?.name ?? '';
      this.editReference = this.editing?.externalReference ?? '';
      this.editingField = null;
      this.saveError = '';
    },
    // Load the notes/comments thread for the anomaly, oldest-first (newest at the bottom). Guarded by a
    // token so a slower response for a previously-selected row can't overwrite the current row's notes.
    async loadComments(markerId) {
      this.comments = [];
      this.isLoadingComments = true;
      const token = ++this._commentsToken;
      try {
        const list = await MarkerService.getCommentByMarkerIdAsync(markerId);
        if (token !== this._commentsToken) return;
        this.comments = (list ?? []).sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
      } catch (e) {
        if (token !== this._commentsToken) return;
        this.commentError = this.actionError(e, 'Could not load notes.');
      } finally {
        if (token === this._commentsToken) this.isLoadingComments = false;
      }
    },
    async addNote() {
      const body = this.newNote.trim();
      if (!body || !this.editing || !this.user) return;
      this.commentError = '';
      this.isAddingNote = true;
      try {
        const payload = new CreateMarkerCommentModel(this.editing.id, body, this.user.name, this.user.sub);
        const created = await MarkerService.createMarkerComment(payload);
        if (created && created.id) {
          this.comments.push(created);
          this.newNote = '';
        } else {
          this.commentError = 'Could not add the note. Please try again.';
        }
      } catch (e) {
        this.commentError = this.actionError(e, 'Could not add the note. Please try again.');
      } finally {
        this.isAddingNote = false;
      }
    },
    noteTimeAgo(dateStr) {
      return dateStr ? format(dateStr) : '';
    },

    // ----- Report -----
    openReportDialog() {
      this.reportError = '';
      this.reportDialog = true;
    },
    // Counts of every individual state over exactly what the table is showing (filteredRows = scope +
    // scan/type/tag filters + search + the active status tab), so the report mirrors the table.
    reportSummary() {
      const source = this.filteredRows;
      let approved = 0, rejected = 0, pending = 0;
      for (const r of source) {
        const s = (r.status || '').toLowerCase();
        if (s === 'approved' || s === 'accepted') approved++;
        else if (s === 'rejected') rejected++;
        else pending++;
      }
      return { approved, rejected, pending, reviewed: approved + rejected, total: source.length };
    },
    // Split a row set into per-status sections (one table each): Approved, Rejected, Pending. Empty
    // sections are dropped, so a table filtered to one status renders just that section.
    reportStatusSections(rows) {
      const isApproved = r => { const s = (r.status || '').toLowerCase(); return s === 'approved' || s === 'accepted'; };
      const isRejected = r => (r.status || '').toLowerCase() === 'rejected';
      const isPendingRow = r => this.isPending(r.status);

      return [
        { title: 'Approved', rows: rows.filter(isApproved) },
        { title: 'Rejected', rows: rows.filter(isRejected) },
        { title: 'Pending', rows: rows.filter(isPendingRow) },
      ].filter(s => s.rows.length);
    },
    // Group the current table view by asset (high-level mode), sorted by asset name.
    reportAssetGroups() {
      const map = new Map();
      for (const r of this.filteredRows) {
        const key = r.assetId || r.assetName || '—';
        if (!map.has(key)) map.set(key, { assetName: r.assetName || 'Unknown asset', rows: [] });
        map.get(key).rows.push(r);
      }
      return [...map.values()].sort((a, b) => (a.assetName || '').localeCompare(b.assetName || ''));
    },
    // Escape untrusted text before injecting into the report HTML.
    reportEscape(value) {
      return String(value ?? '').replace(/[&<>"']/g, c => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    },
    // Print-friendly badge colours for a row's status.
    reportBadge(status) {
      const s = (status || '').toLowerCase();
      if (s === 'approved' || s === 'accepted') return { bg: '#e6f7ee', fg: '#1b7a43', label: 'Approved' };
      if (s === 'rejected') return { bg: '#fdecec', fg: '#c0392b', label: 'Rejected' };
      return { bg: '#fef6e6', fg: '#a76a00', label: 'Pending' };
    },
    reportDate(value) {
      if (!value) return '';
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
    },
    // Comments block embedded in the Name cell (keeps image + text + comments in one <tr> so a
    // page break never splits an anomaly). Oldest-first. Empty string when there are none.
    reportCommentsHtml(comments) {
      if (!comments || !comments.length) return '';
      const esc = this.reportEscape;
      const sorted = [...comments].sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
      const items = sorted.map(c => `
        <div class="rpt-comment">
          <div class="rpt-comment-meta">${esc(c.createdBy || 'Unknown')} · ${esc(this.reportDate(c.createdOn))}</div>
          <div class="rpt-comment-body">${esc(c.comment)}</div>
        </div>`).join('');
      return `<div class="rpt-comments"><div class="rpt-comments-title">Comments (${sorted.length})</div>${items}</div>`;
    },
    // One finding rendered as a compact card (image · name · type/scan/ref · status), so a long
    // report reads as a gallery rather than a dense table.
    reportRowHtml(r, comments, numberById, imgMap) {
      const esc = this.reportEscape;
      const name = r.name ? esc(r.name) : esc(this.rowName(r));
      const ref = r.externalReference ? esc(r.externalReference) : '';
      const type = esc(r.label || 'Anomaly');
      const scan = esc(r.scanName || '');
      const badge = this.reportBadge(r.status);
      const num = numberById ? numberById[r.id] : null;
      let imgHtml = '';
      if (this.reportIncludeImages) {
        const src = (imgMap && imgMap[r.id]) ? imgMap[r.id] : (r.imageSrc || this.reportNoImageSrc);
        imgHtml = `<div class="rpt-card-img-wrap">
            <img class="rpt-img" src="${esc(src)}" alt="Finding thumbnail" />
            ${num != null ? `<span class="rpt-card-num">${num}</span>` : ''}
          </div>`;
      }
      return `
        <div class="rpt-card">
          ${imgHtml}
          <div class="rpt-card-body">
            <div class="rpt-card-head">
              <span class="rpt-card-name">${name}</span>
              <span class="rpt-badge" style="background:${badge.bg};color:${badge.fg}">${esc(badge.label)}</span>
            </div>
            <div class="rpt-card-meta">
              <span>${type}</span>
              ${scan ? `<span>${scan}</span>` : ''}
              ${ref ? `<span class="rpt-card-ref">Ref ${ref}</span>` : ''}
              ${!this.reportIncludeImages && num != null ? `<span class="rpt-card-hash">#${num}</span>` : ''}
            </div>
            ${this.reportCommentsHtml(comments)}
          </div>
        </div>`;
    },
    reportSectionHtml(section, commentMap, numberById, imgMap) {
      const esc = this.reportEscape;
      const head = `<h2 class="rpt-section-title">${esc(section.title)} <span class="rpt-section-count">${section.rows.length}</span></h2>`;
      if (!section.rows.length) {
        return `${head}<p class="rpt-empty">No findings in this state.</p>`;
      }
      const cards = section.rows.map(r => this.reportRowHtml(r, commentMap ? commentMap[r.id] : null, numberById, imgMap)).join('');
      // Comments need width to breathe, so switch to a single column when they're included.
      const gridClass = this.reportIncludeComments ? 'rpt-cards is-detailed' : 'rpt-cards';
      return `${head}<div class="${gridClass}">${cards}</div>`;
    },
    // Downscale + re-encode each finding image (JPEG) so the emailed PDF stays small. Falls back to the
    // original URL if the canvas is cross-origin-tainted (storage without CORS) — worst case = today's size.
    async reportDownscaledImages(rows) {
      const map = {};
      const MAX = 380;
      await Promise.all(rows.map(r => new Promise((resolve) => {
        if (!r.imageSrc) { map[r.id] = null; resolve(); return; }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        const done = (val) => { map[r.id] = val; resolve(); };
        img.onload = () => {
          try {
            const s = Math.min(1, MAX / Math.max(img.naturalWidth || MAX, img.naturalHeight || MAX));
            const w = Math.max(1, Math.round((img.naturalWidth || MAX) * s));
            const h = Math.max(1, Math.round((img.naturalHeight || MAX) * s));
            const c = document.createElement('canvas');
            c.width = w; c.height = h;
            c.getContext('2d').drawImage(img, 0, 0, w, h);
            done(c.toDataURL('image/jpeg', 0.82));
          } catch { done(null); }
        };
        img.onerror = () => done(null);
        img.src = r.imageSrc;
      })));
      return map;
    },
    // Static (print) minimap SVG for the report — a faithful, non-interactive snapshot of the on-page
    // AnomalyMinimap: same framing (plan frames to the WALK PATH + anomalies so it reads zoomed-in, not
    // the full footprint), footprint occupancy cells, coloured per-scan walking path with start dots,
    // level lines (elevation), and dark pill markers. Mirrors AnomalyMinimap's bounds/baseMapper exactly.
    reportMinimapSvg(data) {
      const mode = data.mode || 'plan';
      const fillPts = data.fill || [];
      const levels = data.levels || [];
      const frame = data.frame || null;
      const paths = (data.paths || []).filter(l => l.points && l.points.length >= 2);
      const esc = this.reportEscape;

      // Bounds — identical to AnomalyMinimap.bounds: levels use frame+levels+points; plan frames to the
      // path (+points) when a path exists, else to the footprint fill (+points).
      const us = [], vs = [];
      if (mode === 'levels') {
        if (frame) { us.push(frame.uMin, frame.uMax); vs.push(frame.vMin, frame.vMax); }
        for (const l of levels) vs.push(l.v);
        for (const line of paths) for (const pt of line.points) { us.push(pt[0]); vs.push(pt[1]); }
        for (const p of data.points) { us.push(p.u); vs.push(p.v); }
      } else if (paths.length) {
        for (const line of paths) for (const pt of line.points) { us.push(pt[0]); vs.push(pt[1]); }
        for (const p of data.points) { us.push(p.u); vs.push(p.v); }
      } else {
        for (const p of fillPts) { us.push(p[0]); vs.push(p[1]); }
        for (const p of data.points) { us.push(p.u); vs.push(p.v); }
      }
      if (!us.length) return '<div class="rpt-map-empty">No positioned anomalies</div>';
      const uMin = Math.min(...us), uMax = Math.max(...us), vMin = Math.min(...vs), vMax = Math.max(...vs);
      const uR = Math.max(uMax - uMin, 1e-6), vR = Math.max(vMax - vMin, 1e-6);
      const pad = 9, usable = 100 - 2 * pad;
      let fx, fy;
      if (mode === 'levels') {
        fx = (u) => pad + ((u - uMin) / uR) * usable;
        fy = (v) => pad + ((vMax - v) / vR) * usable;
      } else {
        const span = Math.max(uR, vR), scale = usable / span;
        const offU = (usable - uR * scale) / 2, offV = (usable - vR * scale) / 2;
        fx = (u) => pad + offU + (u - uMin) * scale;
        fy = (v) => pad + offV + (vMax - v) * scale;
      }

      let shapes = '';
      if (mode === 'levels') {
        for (const l of levels) {
          const y = fy(l.v).toFixed(2);
          shapes += `<line x1="6" y1="${y}" x2="94" y2="${y}" stroke="rgba(120,200,190,0.55)" stroke-width="0.4" stroke-dasharray="2 1.5" /><text x="7" y="${(fy(l.v) - 1.4).toFixed(2)}" font-size="3" font-weight="700" fill="#7f95a0">${esc(l.label)}</text>`;
        }
      } else {
        const G = 52, cell = 100 / G;
        const occ = new Set();
        for (const [u, v] of fillPts) {
          const cx = Math.floor(fx(u) / cell), cy = Math.floor(fy(v) / cell);
          if (cx >= 0 && cy >= 0 && cx < G && cy < G) occ.add(cx * G + cy);
        }
        for (const kkey of occ) {
          const cx = Math.floor(kkey / G), cy = kkey % G;
          shapes += `<rect x="${(cx * cell).toFixed(2)}" y="${(cy * cell).toFixed(2)}" width="${(cell + 0.02).toFixed(2)}" height="${(cell + 0.02).toFixed(2)}" fill="rgba(120,200,190,0.42)" />`;
        }
      }

      // Coloured walking path(s) in capture order, one polyline per scan, with start (ST) + finish
      // (FN) dots so the capture direction reads at a glance.
      const endLabel = (x, y, color, label) =>
        `<circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="1.3" fill="${color}" stroke="#12201f" stroke-width="0.4" /><text x="${x.toFixed(2)}" y="${(y - 2.1).toFixed(2)}" font-size="3" font-weight="700" fill="#cfd8dc" text-anchor="middle" paint-order="stroke" stroke="#12201f" stroke-width="0.6">${label}</text>`;
      let pathSvg = '';
      for (const line of paths) {
        const color = esc(line.color || '#ffc65c');
        const poly = line.points.map(([u, v]) => `${fx(u).toFixed(2)},${fy(v).toFixed(2)}`).join(' ');
        pathSvg += `<polyline points="${poly}" fill="none" stroke="${color}" stroke-width="0.5" stroke-linejoin="round" stroke-linecap="round" />`;
        const s = line.points[0], f = line.points[line.points.length - 1];
        pathSvg += endLabel(fx(s[0]), fy(s[1]), color, 'ST');
        pathSvg += endLabel(fx(f[0]), fy(f[1]), color, 'FN');
      }

      // Markers — de-collided so overlapping numbers stay legible, each moved one tied back to its
      // true position with a thin leader line + anchor dot. Dark pill + coloured ring + white number,
      // matching the on-page .minimap-marker.
      const laid = deCollideLabels(
        data.points.map(p => ({ ...p, x: fx(p.u), y: fy(p.v) })),
        { minSep: 6.5 },
      );
      let leaders = '', dots = '';
      for (const p of laid) {
        const color = esc(p.color || '#64ffda');
        const lx = p.lx.toFixed(2), ly = p.ly.toFixed(2);
        if (p.moved) {
          leaders += `<line x1="${lx}" y1="${ly}" x2="${p.x.toFixed(2)}" y2="${p.y.toFixed(2)}" stroke="rgba(255,255,255,0.5)" stroke-width="0.4" /><circle cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="0.9" fill="${color}" stroke="#12201f" stroke-width="0.3" />`;
        }
        dots += `<circle cx="${lx}" cy="${ly}" r="3" fill="#12201f" stroke="${color}" stroke-width="1" /><text x="${lx}" y="${ly}" font-size="3" font-weight="700" fill="#ffffff" text-anchor="middle" dominant-baseline="central">${p.number}</text>`;
      }

      return `<svg class="rpt-map-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet"><rect x="0" y="0" width="100" height="100" fill="#1a262d" stroke="#e2e8ec" stroke-width="0.5" />${shapes}${pathSvg}${leaders}${dots}</svg>`;
    },
    // The report minimap tiles (plot plan + side elevation) built from the numbered report rows.
    // Markers are transformed to the version world frame (multi-scan safe); the footprint uses the
    // combined stored slice (nothing drawn until it's captured). Returns just the two tiles so they
    // can sit in the shared overview row alongside the summary (see the 2/5/5 grid in generateReport).
    reportMinimapTiles(rows, numberById) {
      const project = (view) => rows
        .filter(r => Number.isFinite(r.x) && Number.isFinite(r.y) && Number.isFinite(r.z))
        .map(r => {
          const w = this.toWorld(r.scanId, r.x, r.y, r.z);
          return { u: w.x, v: view === 'plot' ? w.y : w.z, number: numberById[r.id], color: this.statusDotColor(r.status), label: r.label };
        });
      const planCombined = this.combinedPerimeter('plot');
      // Plot plan only — side elevation was removed in favour of per-marker Low/Medium/High bands.
      const planSvg = this.reportMinimapSvg({ points: project('plot'), fill: planCombined ? planCombined.fill : [], paths: this.minimapPathsPlan });
      return `<div class="rpt-overview-map"><div class="rpt-map-caption">Plot plan (X · Y)</div>${planSvg}</div>`;
    },
    async generateReport() {
      this.reportError = '';

      const esc = this.reportEscape;

      // Brand mark — same dynamic logo as the navbar. Custom org logo when the URL isn't the Duallo
      // default; only then does the "powered by duallo.ai" mark show. The logos are white raster, so
      // the report renders them black via a CSS filter. Resolve to an absolute URL since the report
      // lives in a blank window (relative/webpack paths wouldn't resolve there). Computed up front so
      // the loading screen can show it too.
      const rawLogo = this.organizationStore?.organizationLogoUrl || '';
      const isCustomLogo = !!rawLogo && !rawLogo.includes('duallo-logo-white.png');
      let logoSrc = '';
      if (rawLogo) {
        try { logoSrc = new URL(rawLogo, window.location.origin).href; } catch { logoSrc = rawLogo; }
      }
      const brandHtml = `
        <div class="rpt-brand">
          ${logoSrc ? `<img class="rpt-logo" src="${esc(logoSrc)}" alt="Logo" />` : ''}
          ${isCustomLogo ? `<div class="rpt-powered">powered by <span>duallo.ai</span></div>` : ''}
        </div>`;

      // Open the window NOW, inside the click gesture — opening it after the (awaited) comment fetch
      // would be blocked by pop-up blockers. Seed a branded, animated loading screen while we gather data.
      const win = window.open('', '_blank');
      if (!win) {
        this.reportError = 'Could not open the report window — please allow pop-ups for this site and try again.';
        return;
      }
      const loadingHtml = `<!doctype html><html><head><meta charset="utf-8" /><title>Generating findings report…</title>
        <style>
          html, body { height: 100%; margin: 0; }
          body { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 22px;
                 font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; background: #fff; }
          .ld-logo { height: 50px; width: auto; filter: brightness(0); }
          .ld-spin { width: 40px; height: 40px; border: 4px solid #e2e8ec; border-top-color: #1a2226;
                     border-radius: 50%; animation: ld-rot 0.9s linear infinite; }
          .ld-text { font-size: 14px; color: #6b7d85; letter-spacing: .04em; }
          @keyframes ld-rot { to { transform: rotate(360deg); } }
        </style></head>
        <body>
          ${logoSrc ? `<img class="ld-logo" src="${esc(logoSrc)}" alt="Logo" />` : ''}
          <div class="ld-spin"></div>
          <div class="ld-text">Generating report…</div>
        </body></html>`;
      win.document.open();
      win.document.write(loadingHtml);
      win.document.close();

      // Fetch comments for the anomalies that will actually appear (selected statuses), de-duped by id.
      let commentMap = null;
      if (this.reportIncludeComments) {
        this.isGeneratingReport = true;
        try {
          const rowsForComments = this.filteredRows;
          const uniqueRows = [...new Map(rowsForComments.map(r => [r.id, r])).values()];
          commentMap = {};
          await Promise.all(uniqueRows.map(async (r) => {
            try {
              commentMap[r.id] = await MarkerService.getCommentByMarkerIdAsync(r.id);
            } catch {
              commentMap[r.id] = [];
            }
          }));
        } finally {
          this.isGeneratingReport = false;
        }
      }

      // Downscale finding images up front (keeps the emailed PDF small). Runs in the main window
      // where a canvas is available; the resulting data URLs embed straight into the report.
      let imgMap = null;
      if (this.reportIncludeImages) {
        this.isGeneratingReport = true;
        try {
          const renderRows = this.filteredRows;
          const uniqueRows = [...new Map(renderRows.map(r => [r.id, r])).values()];
          imgMap = await this.reportDownscaledImages(uniqueRows);
        } finally {
          this.isGeneratingReport = false;
        }
      }

      const summary = this.reportSummary();
      const assetName = esc(this.headingTitle || 'Asset');
      const generatedOn = new Date().toLocaleString();
      const preparedBy = esc(this.user?.name || this.user?.email || '');
      const scopeLabel = this.isAssetLocked ? 'All scans of this asset' : 'All assets in scope';
      // One-line executive summary for the top of the document.
      const pct = summary.total ? Math.round((summary.reviewed / summary.total) * 100) : 0;
      const execSummary = summary.total
        ? `${summary.total} findings — ${summary.pending} pending review, ${summary.approved} approved, ${summary.rejected} rejected (${pct}% reviewed).`
        : 'No findings recorded for the current scope.';

      // Body: at the high level (org mode) group by asset, each with its per-status tables. When
      // locked to a single asset, just the per-status tables — plus an optional minimap + a map-number
      // column numbering the anomalies in report reading order (so a row's # matches its map circle).
      const withMinimap = this.isAssetLocked && this.reportIncludeMinimap;
      let bodyHtml;
      let minimapTiles = '';
      if (this.isAssetLocked) {
        const sections = this.reportStatusSections(this.filteredRows);
        let numberById = null;
        if (withMinimap) {
          const ordered = [];
          const seen = new Set();
          for (const s of sections) for (const r of s.rows) if (!seen.has(r.id)) { seen.add(r.id); ordered.push(r); }
          numberById = {};
          ordered.forEach((r, i) => { numberById[r.id] = i + 1; });
          minimapTiles = this.reportMinimapTiles(ordered, numberById);
        }
        bodyHtml = sections.map(s => this.reportSectionHtml(s, commentMap, numberById, imgMap)).join('');
      } else {
        const groups = this.reportAssetGroups();
        bodyHtml = groups.length
          ? groups.map(g => {
            const tables = this.reportStatusSections(g.rows).map(s => this.reportSectionHtml(s, commentMap, null, imgMap)).join('');
            return `<section class="rpt-asset-group"><h2 class="rpt-asset-heading">${esc(g.assetName)} <span class="rpt-asset-count">${g.rows.length}</span></h2>${tables}</section>`;
          }).join('')
          : '<p class="rpt-empty">No findings match the current view.</p>';
      }

      const tile = (label, value, cls = '') =>
        `<div class="rpt-tile ${cls}"><div class="rpt-tile-num">${value}</div><div class="rpt-tile-label">${label}</div></div>`;

      const summaryTiles = `
        ${tile('Total', summary.total, 'is-total')}
        ${tile('Approved', summary.approved, 'is-approved')}
        ${tile('Rejected', summary.rejected, 'is-rejected')}
        ${tile('Pending', summary.pending, 'is-pending')}
        ${tile('Reviewed', summary.reviewed)}`;

      // With a minimap, the summary sits in a narrow left column (2/12) beside the two smaller maps
      // (5/12 each) as one overview row. Without a minimap, the tiles keep their full-width strip.
      const summaryHtml = minimapTiles
        ? `<section class="rpt-overview">
             <div class="rpt-overview-summary">${summaryTiles}</div>
             ${minimapTiles}
           </section>`
        : `<section class="rpt-summary">${summaryTiles}</section>`;

      const html = `<!doctype html>
        <html>
        <head>
          <meta charset="utf-8" />
          <title>Findings Report — ${assetName}</title>
          <style>
            * { box-sizing: border-box; }
            /* Portrait, no fixed paper size → adapts to whatever the printer holds (A4 or US Letter);
               mm margins fit both. Content tables are width:100%, so they reflow to the printable width. */
            @page { size: portrait; margin: 16mm; }
            body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #1a2226; margin: 28px; }
            .rpt-page { width: 100%; border-collapse: collapse; }
            .rpt-page-cell, .rpt-foot-cell { padding: 0; }
            .rpt-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 12px; padding-top: 8px; border-top: 1px solid #d7e0e4; font-size: 10px; color: #6b7d85; }
            .rpt-footer-left { font-weight: 600; flex: 1 1 0; }
            .rpt-footer-right { flex: 1 1 0; text-align: right; }
            .rpt-footer-brand { flex: 0 0 auto; }
            .rpt-footer-brand span { color: #1a2226; font-weight: 700; }
            .rpt-header { border-bottom: 2px solid #e2e8ec; padding-bottom: 14px; margin-bottom: 18px; }
            .rpt-brand { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; margin-bottom: 14px; }
            /* Navbar logos are white raster — brightness(0) renders them solid black for the report. */
            .rpt-logo { height: 40px; width: auto; filter: brightness(0); -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .rpt-powered { font-size: 11px; color: #6b7d85; }
            .rpt-powered span { color: #1a2226; font-weight: 700; }
            .rpt-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: #6b7d85; }
            .rpt-title { font-size: 24px; font-weight: 800; margin: 4px 0 2px; }
            .rpt-meta { font-size: 12px; color: #6b7d85; }
            .rpt-lead { font-size: 13.5px; color: #3a464c; line-height: 1.5; margin: 14px 0 0; }
            .rpt-summary { display: flex; flex-wrap: wrap; gap: 12px; margin: 18px 0 26px; }
            /* Overview row: summary (4/12) on the left, the two smaller minimap squares (4/12 each). */
            .rpt-overview { display: grid; grid-template-columns: 4fr 8fr; gap: 16px; align-items: start; margin: 18px 0 26px; break-inside: avoid; page-break-inside: avoid; }
            .rpt-overview-summary { display: flex; flex-direction: column; gap: 8px; }
            .rpt-overview-summary .rpt-tile { flex: 0 0 auto; padding: 10px 12px; }
            .rpt-overview-summary .rpt-tile-num { font-size: 22px; }
            .rpt-overview-map { min-width: 0; }
            .rpt-tile { flex: 1 1 110px; border: 1px solid #e2e8ec; border-radius: 10px; padding: 12px 14px; background: #f8fafb; }
            .rpt-tile-num { font-size: 26px; font-weight: 800; line-height: 1; }
            .rpt-tile-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #6b7d85; margin-top: 6px; }
            .rpt-tile.is-total .rpt-tile-num { color: #0f172a; }
            .rpt-tile.is-approved .rpt-tile-num { color: #1b7a43; }
            .rpt-tile.is-rejected .rpt-tile-num { color: #c0392b; }
            .rpt-tile.is-pending .rpt-tile-num { color: #a76a00; }
            .rpt-section-title { font-size: 17px; font-weight: 800; margin: 26px 0 10px; padding-bottom: 6px; border-bottom: 1px solid #e2e8ec; }
            .rpt-section-count { display: inline-block; font-size: 12px; font-weight: 700; color: #6b7d85; background: #eef2f4; border-radius: 999px; padding: 2px 9px; margin-left: 6px; vertical-align: middle; }
            .rpt-empty { color: #8aa0aa; font-size: 13px; margin: 4px 0 8px; }
            .rpt-table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
            .rpt-table th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #6b7d85; border-bottom: 1px solid #d7e0e4; padding: 8px 10px; }
            .rpt-table td { border-bottom: 1px solid #eef2f4; padding: 10px; font-size: 13px; vertical-align: middle; }
            .rpt-table tr { break-inside: avoid; page-break-inside: avoid; }
            .rpt-img-cell { width: 196px; }
            .rpt-img { width: 176px; height: 176px; object-fit: contain; background: #f1f5f7; border: 1px solid #e2e8ec; border-radius: 8px; display: block; }
            .rpt-asset-group { margin-top: 8px; }
            .rpt-asset-heading { font-size: 20px; font-weight: 800; margin: 34px 0 6px; padding-bottom: 8px; border-bottom: 2px solid #1a2226; break-after: avoid; page-break-after: avoid; }
            .rpt-asset-count { display: inline-block; font-size: 12px; font-weight: 700; color: #6b7d85; background: #eef2f4; border-radius: 999px; padding: 2px 9px; margin-left: 6px; vertical-align: middle; }
            .rpt-name { font-weight: 700; }
            .rpt-ref { font-family: ui-monospace, Consolas, monospace; }
            .rpt-asset { font-weight: 600; white-space: nowrap; }
            .rpt-num { width: 30px; text-align: center; font-weight: 800; color: #1a2226; }
            /* Finding cards (2-up, single column when comments are included). */
            .rpt-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 10px 0 16px; }
            .rpt-cards.is-detailed { grid-template-columns: 1fr; }
            .rpt-card { display: flex; border: 1px solid #e2e8ec; border-radius: 10px; overflow: hidden; background: #fff; break-inside: avoid; page-break-inside: avoid; }
            .rpt-card-img-wrap { position: relative; flex: 0 0 118px; }
            .rpt-card .rpt-img { width: 118px; height: 118px; object-fit: cover; display: block; background: #f1f5f7; border: none; border-radius: 0; }
            .rpt-card-num { position: absolute; top: 6px; left: 6px; min-width: 20px; height: 20px; padding: 0 5px; border-radius: 999px; background: #1a2226; color: #fff; font-size: 11px; font-weight: 800; display: inline-flex; align-items: center; justify-content: center; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .rpt-card-body { flex: 1 1 auto; min-width: 0; padding: 11px 13px; display: flex; flex-direction: column; gap: 7px; }
            .rpt-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
            .rpt-card-name { font-size: 14px; font-weight: 700; color: #1a2226; word-break: break-word; }
            .rpt-card-head .rpt-badge { flex: 0 0 auto; }
            .rpt-card-meta { display: flex; flex-wrap: wrap; gap: 3px 12px; font-size: 12px; color: #566169; }
            .rpt-card-ref, .rpt-card-hash { font-family: ui-monospace, Consolas, monospace; }
            .rpt-map { margin: 20px 0 8px; break-inside: avoid; page-break-inside: avoid; }
            .rpt-map-title { font-size: 17px; font-weight: 800; margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid #e2e8ec; }
            .rpt-map-grid { display: flex; gap: 16px; }
            .rpt-map-tile { flex: 1 1 0; min-width: 0; }
            .rpt-map-caption { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #6b7d85; margin-bottom: 6px; }
            .rpt-map-svg { width: 100%; aspect-ratio: 1 / 1; display: block; border-radius: 8px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .rpt-map-empty { display: flex; align-items: center; justify-content: center; aspect-ratio: 1 / 1; background: #1a262d; border: 1px solid #e2e8ec; border-radius: 8px; color: #9fb1b8; font-size: 12px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .rpt-badge { display: inline-block; font-size: 12px; font-weight: 700; border-radius: 999px; padding: 3px 12px; }
            .rpt-comments { margin-top: 10px; padding: 8px 10px; background: #f5f8f9; border: 1px solid #e2e8ec; border-radius: 8px; font-weight: 400; }
            .rpt-comments-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #6b7d85; margin-bottom: 6px; }
            .rpt-comment { padding: 5px 0; border-top: 1px solid #eaeff1; }
            .rpt-comment:first-of-type { border-top: none; }
            .rpt-comment-meta { font-size: 11px; color: #7a8a90; }
            .rpt-comment-body { font-size: 12px; color: #2a343a; white-space: pre-wrap; }
            @media print { body { margin: 0; } .rpt-tile { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; } .rpt-badge, .rpt-tile-num { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <!-- Wrapped in a table so the <tfoot> repeats on every printed page (the one cross-browser
               way to get a running footer) and reserves its space so content never overlaps it. -->
          <table class="rpt-page">
            <tbody>
              <tr><td class="rpt-page-cell">
                <header class="rpt-header">
                  ${brandHtml}
                  <div class="rpt-eyebrow">Findings Report</div>
                  <div class="rpt-title">${assetName}</div>
                  <div class="rpt-meta">
                    ${scopeLabel} · Generated ${esc(generatedOn)}${preparedBy ? ` · Prepared by ${preparedBy}` : ''}
                  </div>
                </header>
                <p class="rpt-lead">${esc(execSummary)}</p>
                ${summaryHtml}
                ${bodyHtml}
              </td></tr>
            </tbody>
            <tfoot>
              <tr><td class="rpt-foot-cell">
                <div class="rpt-footer">
                  <span class="rpt-footer-left">${assetName} — Findings Report</span>
                  <span class="rpt-footer-brand">${isCustomLogo ? 'powered by ' : ''}<span>duallo.ai</span></span>
                  <span class="rpt-footer-right">Generated ${esc(generatedOn)}</span>
                </div>
              </td></tr>
            </tfoot>
          </table>
        </body>
        </html>`;

      // Replace the placeholder in the already-open window with the finished report.
      win.document.open();
      win.document.write(html);
      win.document.close();

      // Wait for every image before printing, and give anomaly images the same fallback the on-page
      // table has: a broken/expired SAS thumbnail (no onerror inside the report window) is swapped to
      // the shared no-image placeholder instead of rendering as a broken-image icon in the PDF.
      const fallbackSrc = this.reportNoImageSrc;
      const waitForImage = (img) => new Promise((resolve) => {
        const canFallback = img.classList.contains('rpt-img');
        const swapToFallback = () => { img.onerror = null; img.onload = () => resolve(); img.src = fallbackSrc; };
        if (img.complete) {
          if (canFallback && img.naturalWidth === 0 && img.src !== fallbackSrc) swapToFallback();
          else resolve();
          return;
        }
        img.onload = () => resolve();
        img.onerror = () => { if (canFallback && img.src !== fallbackSrc) swapToFallback(); else resolve(); };
      });
      const triggerPrint = () => {
        Promise.all(Array.from(win.document.images).map(waitForImage))
          .then(() => { win.focus(); win.print(); });
      };
      if (win.document.readyState === 'complete') triggerPrint();
      else win.addEventListener('load', triggerPrint);

      this.reportDialog = false;
    },
    async saveDetails() {
      if (!this.editing) return;
      this.saveError = '';
      this.isSavingName = true;
      try {
        const name = this.editName.trim() || null;
        const externalReference = this.editReference.trim() || null;
        const updated = await AssetService.updateAnomalyDetails(this.editing.scanId, this.editing.id, { name, externalReference });
        const savedName = updated ? updated.name : name;
        const savedRef = updated ? updated.externalReference : externalReference;
        // `editing` is the same object reference as the row in `rows`, so this updates the table too.
        this.editing.name = savedName;
        this.editing.externalReference = savedRef;
        // Re-sync the drafts to the persisted (trimmed) values, then close the inline editor.
        this.editName = savedName ?? '';
        this.editReference = savedRef ?? '';
        this.editingField = null;
      } catch (e) {
        this.saveError = this.actionError(e, 'Could not save the details. Please try again.');
      } finally {
        this.isSavingName = false;
      }
    },
    // Anything not already accepted/rejected can be reviewed.
    isPending(status) {
      const s = (status || '').toLowerCase();
      return s !== 'approved' && s !== 'accepted' && s !== 'rejected';
    },
    // Dialog (mobile) review entry: confirm before rejecting, then apply.
    async confirmReviewAnomaly(status) {
      if (!this.editing) return;
      if (status === 'Rejected') {
        const ok = await this.confirm({
          title: 'Reject finding?',
          message: `Mark "${this.rowName(this.editing)}" as rejected.`,
          confirmText: 'Reject', cancelText: 'Cancel', danger: true, icon: 'mdi-close-circle-outline',
        });
        if (!ok) return;
      }
      await this.reviewAnomaly(status);
    },
    async reviewAnomaly(status) {
      if (!this.editing) return;
      this.saveError = '';
      this.isReviewing = true;
      try {
        const updated = await AssetService.reviewAnomaly(this.editing.scanId, this.editing.id, status);
        const newStatus = updated ? updated.status : status;
        // `editing` is the same object reference as the row in `rows`, so this updates both the
        // dialog and the table (the review buttons disappear once it's no longer pending).
        this.editing.status = newStatus;
        this.reReviewIds = this.reReviewIds.filter(id => id !== this.editing.id);
        this.showReviewToast(`“${this.rowName(this.editing)}” ${(status || '').toLowerCase() === 'rejected' ? 'rejected' : 'approved'}`, status);
      } catch (e) {
        this.saveError = this.actionError(e, 'Could not update the review status. Please try again.');
        this.showReviewToast(`Couldn’t update “${this.rowName(this.editing)}” — please try again`, 'error');
      } finally {
        this.isReviewing = false;
      }
    },
    // Surface the HTTP status alongside the message so environment issues are obvious
    // (404 = endpoint not deployed, 403 = missing permission, 5xx = server error).
    actionError(e, fallback) {
      const msg = e?.message || fallback;
      return e?.status ? `(${e.status}) ${msg}` : msg;
    },
    async resolveLockedAsset() {
      // Prefer the asset already in the store (set when navigating from the landing page).
      const stored = this.assetStore?.asset;
      if (stored?.id === this.lockedAssetId && stored?.name) {
        this.lockedAssetName = stored.name;
        return;
      }
      try {
        const asset = await AssetService.getAssetByIdAsync(this.organizationStore.organizationId, this.lockedAssetId);
        if (asset?.name) this.lockedAssetName = asset.name;
      } catch {
        this.lockedAssetName = 'Asset';
      }
    },
    async loadOrgScopeData() {
      const organizationId = this.organizationStore.organizationId;
      const [assets, sites] = await Promise.all([
        AssetService.getAssetsByOrganizationIdAsync(organizationId).catch(() => []),
        // Sites feed the scope dropdown; optional (needs site:view), degrade to no site filter.
        SiteService.getSitesByOrganizationId(organizationId).catch(() => []),
      ]);
      this.assets = assets ?? [];
      this.sites = sites ?? [];
    },
    // Org tags for the tag filter. Loaded once; the filter only offers tags that are actually present
    // on loaded rows (see tagOptions), so this is just the metadata lookup (name/colour/icon).
    async loadTags() {
      const orgId = this.organizationStore?.organizationId;
      if (!orgId) return;
      this.allTags = await TagService.getTagsByOrganizationId(orgId).catch(() => []) ?? [];
    },
    async loadForScope() {
      const scoped = this.scopedAssets;
      this.isLoading = true;
      this.expandedId = null;   // reset the pinned thumbnail when the row set changes
      this.currentPage = 1;
      try {
        if (!scoped.length) {
          this.rows = [];
          this.scanOptions = [];
          return;
        }

        // Fan out across every scoped asset: markers + scan names per asset. Scans are limited to the
        // CURRENT model version (only fetched when a single asset is in scope, where the scan filter
        // shows). Markers are already current-version (the API scopes them to the current version).
        const singleAsset = scoped.length === 1;
        const perAsset = await Promise.all(scoped.map(async (a) => {
          const [markers, allScans, currentVersion] = await Promise.all([
            AssetService.getAnomalyMarkersByAssetId(a.id).catch(() => []),
            ModelVersionService.getScansByAssetId(a.id).catch(() => []),
            singleAsset ? ModelVersionService.getCurrentModelVersion(a.id).catch(() => null) : Promise.resolve(null),
          ]);
          let scans = allScans ?? [];
          if (currentVersion) {
            const currentIds = new Set((currentVersion.scans ?? []).map(s => s.scanId).filter(Boolean));
            if (currentIds.size) scans = scans.filter(s => currentIds.has(s.id));
          }
          const scanNames = Object.fromEntries(scans.map(s => [s.id, s.name]));
          const rows = (markers ?? []).map(m => ({
            id: m.id,
            label: m.label,
            name: m.name,
            externalReference: m.externalReference,
            assetId: a.id,
            assetName: a.name,
            siteTitle: a.siteTitle,
            scanId: m.scanId,
            scanName: scanNames[m.scanId] ?? 'Unknown scan',
            status: m.status,
            createdOn: m.createdOn,   // used to sort newest-first
            color: this.resolveColor(m),
            thumbnailPath: m.thumbnailPath,
            imageSrc: null,
            isAIGenerated: m.isAIGenerated,   // gates the "source image" (AI provenance) viewer
            tagIds: m.tagIds ?? [],           // assigned org-tag ids, for the tag filter

            // Scan-local marker position, for the minimap. (Multi-scan assets will need the per-scan
            // version transform applied — carried in from the model version in a later pass.)
            x: m.transform?.position?.x,
            y: m.transform?.position?.y,
            z: m.transform?.position?.z,
          }));
          return { scans: scans ?? [], rows };
        }));

        // Default order: pending first (the job to do), oldest first within each group.
        const rows = perAsset.flatMap(p => p.rows).sort((a, b) => {
          const pa = this.isPending(a.status) ? 0 : 1;
          const pb = this.isPending(b.status) ? 0 : 1;
          if (pa !== pb) return pa - pb;
          return new Date(a.createdOn) - new Date(b.createdOn);
        });

        // Scan filter is only meaningful for a single asset in scope.
        this.scanOptions = scoped.length === 1
          ? perAsset[0].scans.map(s => ({ id: s.id, name: s.name }))
          : [];

        // Sign each thumbnail (token is cached after the first call, so this is cheap).
        await Promise.all(rows.map(async (r) => {
          r.imageSrc = r.thumbnailPath ? await organizationSasService.getSignedUrl(r.thumbnailPath) : null;
        }));

        this.rows = rows;
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to load anomalies for management view', e);
        }
        this.rows = [];
      } finally {
        this.isLoading = false;
      }
    },
    resolveColor(marker) {
      const byId = getMarkerStyleById(marker.markerTypeId);
      const byLabel = getMarkerStyle(marker.label);
      return marker.color || byId.color || byLabel.color || '#5E94A8';
    },
    statusClass(status) {
      const s = (status || '').toLowerCase();
      if (s === 'approved' || s === 'accepted') return 'is-approved';
      if (s === 'rejected') return 'is-rejected';
      return 'is-pending';
    },
    onBack() {
      if (this.isAssetLocked) {
        this.$router.push({ name: 'AssetLanding', params: { id: this.lockedAssetId } });
      } else {
        // "Assets" is the home page route (see router/index.js).
        this.$router.push({ name: 'Assets' });
      }
    },
    // Always the assets list (the breadcrumb's top-level "Assets" segment).
    goHome() {
      this.$router.push({ name: 'Assets' });
    },
    // Desktop/tablet: the right rail is the details surface, so mirror the selected row into the same
    // edit/notes state the (mobile) dialog uses, and load its notes. No-op on phones (dialog handles it).
    syncInlineDetail() {
      if (!this.showRail) return;
      const row = this.currentRow;
      if (!row) { this.editing = null; this.comments = []; this.newNote = ''; return; }
      if (this.editing && this.editing.id === row.id) return;
      this.editing = row;
      this.editName = row.name ?? '';
      this.editReference = row.externalReference ?? '';
      this.editingField = null;
      this.saveError = '';
      this.newNote = '';
      this.commentError = '';
      this.loadComments(row.id);
    },
  },
};
</script>

<style scoped>
.page-section {
  margin-bottom: 24px;
}

.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
}

.page-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #f0b54a;
}

.page-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #fff;
  margin: 2px 0 0;
}

.page-sub {
  font-size: 0.9rem;
  color: #8aa0aa;
  margin: 6px 0 0;
}

.content-layout {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

/* The table card flexes; when the minimap is present it takes the remaining width (so the table is
   narrower). min-width:0 lets the table's own horizontal scroll kick in instead of overflowing. */
.content-layout > .register-card {
  flex: 1 1 auto;
  min-width: 0;
}

.minimap-col {
  flex: 0 0 480px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: sticky;
  top: 16px;
}

.minimap-note {
  font-size: 0.72rem;
  color: #6b7d85;
  line-height: 1.45;
  margin: 0;
}

.register-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.lock-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(240, 181, 74, 0.1);
  border: 1px solid rgba(240, 181, 74, 0.28);
  color: #f0d19a;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}

.scope-bar {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px 16px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.12);
}

.scope-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 200px;
  flex: 1 1 200px;
  max-width: 320px;
}

.scope-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.scope-field .scan-select {
  width: 100%;
  min-width: 0;
}

.filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.filter-group {
  display: inline-flex;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 3px;
}

.seg-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  background: transparent;
  color: #8aa0aa;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 6px 10px 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.seg-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #cfd8dc;
  font-size: 0.7rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.seg-btn.active {
  background: rgba(240, 181, 74, 0.16);
  color: #f0b54a;
}

.seg-btn.active .seg-count {
  background: rgba(240, 181, 74, 0.25);
  color: #f5c76a;
}

.scan-select,
.search-input {
  background-color: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: #e3eef0;
  font-size: 0.85rem;
  height: 38px;
  padding: 0 10px;
  outline: none;
  transition: border-color 0.2s;
}

.scan-select:hover,
.search-input:hover,
.scan-select:focus,
.search-input:focus {
  border-color: rgba(240, 181, 74, 0.45);
}

.scan-select {
  /* Drop the native chrome and supply our own chevron; color-scheme:dark makes the native
     option list render dark to match the panel instead of the OS default white. */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  color-scheme: dark;
  cursor: pointer;
  min-width: 160px;
  padding-right: 30px;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238aa0aa' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
}

.scan-select option {
  background: #1c272c;
  color: #e3eef0;
}

.search-input {
  flex: 1 1 180px;
  min-width: 140px;
}

.search-input::placeholder {
  color: #6b7d85;
}

.report-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  background: rgba(240, 181, 74, 0.12);
  border: 1px solid rgba(240, 181, 74, 0.35);
  color: #f0d19a;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.report-btn:hover {
  background: rgba(240, 181, 74, 0.2);
  border-color: rgba(240, 181, 74, 0.55);
  color: #f7dca8;
}

/* ----- Report dialog ----- */
.report-sub {
  font-size: 0.88rem;
  color: #b9c6cc;
  margin: 0;
  line-height: 1.5;
}

.report-options {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.report-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8aa0aa;
  margin-top: 4px;
}

.report-check {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.05);
  color: #e3eef0;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.report-check input {
  width: 16px;
  height: 16px;
  accent-color: var(--duallo-accent);
  cursor: pointer;
}

.report-check.report-extra {
  margin-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0;
  background: transparent;
  padding-top: 14px;
}

.report-check.is-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.report-check.is-disabled input {
  cursor: not-allowed;
}

.table-wrap {
  overflow-x: auto;
}

.register-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}

.register-table thead th {
  text-align: left;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #7f95a0;
  padding: 11px 14px;
  background: rgba(0, 0, 0, 0.18);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  white-space: nowrap;
}

.register-table tbody td {
  padding: 10px 14px;
  color: #d6e0e4;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  vertical-align: middle;
}

.register-table tbody tr:last-child td {
  border-bottom: none;
}

.register-table tbody tr.data-row {
  transition: background 0.12s ease;
}

/* Subtle zebra for scan-ability; hover/selected tints sit above it. */
.register-table tbody tr:nth-child(even) {
  background: rgba(255, 255, 255, 0.014);
}

.register-table tbody tr:hover {
  background: rgba(100, 255, 218, 0.045);
}

.col-thumb {
  width: 64px;
  padding-right: 0;
}

.thumb {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  background: #1c272c;
  border: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: zoom-in;   /* hover previews; click pins enlarged */
  /* Only height animates. Width snaps instantly so the shared column width never dips mid-
     transition when switching pinned rows (which would shove the other columns sideways). */
  transition: height 0.2s ease;
}

/* Click-to-pin: the thumbnail grows in place, making the row taller. Selecting another pins that
   one and collapses the previous in the same tick, so heights animate together. */
.thumb.is-expanded {
  width: 200px;
  height: 200px;
  cursor: zoom-out;
}

.thumb-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.15s ease;
}

.thumb:hover .thumb-image {
  transform: scale(1.06);
}

/* Show the whole anomaly (not a cropped square) once pinned large; no hover-zoom on the pinned one. */
.thumb.is-expanded .thumb-image {
  object-fit: contain;
  transform: none;
}

.thumb-empty {
  cursor: default;
}

/* The no-image placeholder graphic reads better shown whole than cropped. */
.thumb-empty .thumb-image {
  object-fit: contain;
}

/* Hover preview popover (teleported by v-tooltip, so it escapes the table's overflow clip). */
:deep(.thumb-preview-tip) {
  padding: 4px !important;
  background: #0f171b !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 10px !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55) !important;
  opacity: 1 !important;
}

.thumb-preview-img {
  display: block;
  width: 260px;
  height: 260px;
  object-fit: contain;
  border-radius: 6px;
  background: #1c272c;
}

.col-type {
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
}

.col-status {
  width: 100px;
}

/* Uniform-width, centred status pills so the column reads as a tidy stack. */
.col-status .duallo-status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 78px;
}

.type-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  vertical-align: middle;
}

.scan-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(102, 199, 180, 0.1);
  border: 1px solid rgba(102, 199, 180, 0.22);
  color: #9fe3d6;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.78rem;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scan-chip-icon {
  flex: 0 0 auto;
}

.col-name {
  min-width: 120px;
  max-width: 200px;
}

.name-text {
  color: #fff;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
}

/* Auto-generated identity for unnamed anomalies — dimmer + lighter so it reads as a placeholder,
   not a user-set name. */
.name-text.is-auto-name {
  color: #8aa0aa;
  font-weight: 500;
  font-style: italic;
}

.col-ref {
  min-width: 100px;
  max-width: 160px;
}

.ref-text {
  color: #cfd8dc;
  font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
  font-size: 0.82rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
}
.ref-empty { color: #5f727a; }

.col-edit {
  width: 108px;
  text-align: right;
}

.modify-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #cfd8dc;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.modify-btn:hover {
  background: rgba(100, 255, 218, 0.08);
  border-color: rgba(100, 255, 218, 0.3);
  color: #fff;
}

.col-asset {
  width: 190px;
  max-width: 190px;
}

.asset-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(100, 255, 218, 0.08);
  border: 1px solid rgba(100, 255, 218, 0.2);
  color: #9fe6d6;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 0.74rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-site {
  width: 180px;
  max-width: 180px;
}

.site-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(240, 181, 74, 0.1);
  border: 1px solid rgba(240, 181, 74, 0.28);
  color: #f0d19a;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 0.74rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Status pill now uses the shared global .duallo-status-pill (.is-approved/.is-rejected/.is-pending)
   from duallo-components.css — the per-file copy was removed. */

.source-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #9fb1b8;
  font-size: 0.8rem;
}

.muted {
  color: #5a6970;
}

.row-btn {
  color: var(--duallo-accent) !important;
  font-weight: 600 !important;
  text-transform: none !important;
}

.register-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;
  font-size: 0.78rem;
  color: #7d909a;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.page-size {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.page-size-label {
  color: #8aa0aa;
  font-weight: 600;
}

.page-size-select {
  min-width: 72px;
  height: 32px;
}

.pager {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.pager-label {
  color: #cfd8dc;
  font-weight: 600;
  min-width: 92px;
  text-align: center;
}

.pager-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #cfd8dc;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.pager-btn:hover:not(:disabled) {
  background: rgba(100, 255, 218, 0.08);
  border-color: rgba(100, 255, 218, 0.3);
  color: #fff;
}

.pager-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.state-block {
  padding: 28px 16px;
}

.row-skeleton {
  height: 44px;
  border-radius: 8px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.03) 25%, rgba(255, 255, 255, 0.06) 37%, rgba(255, 255, 255, 0.03) 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;
}

@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
}

.empty-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #cfd8dc;
  margin: 10px 0 2px;
}

.empty-subtitle {
  font-size: 0.82rem;
  color: #8aa0aa;
  margin: 0;
  max-width: 360px;
}

/* ----- Details / rename dialog ----- */
.detail-modal {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #fff;
}

.detail-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Two-column body: details on the left, notes thread on the right. */
.detail-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 18px;
}

.detail-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.detail-col-notes {
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  padding-left: 18px;
}

.notes-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.notes-title {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.notes-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(100, 255, 218, 0.12);
  border: 1px solid rgba(100, 255, 218, 0.25);
  color: var(--duallo-accent);
  font-size: 0.74rem;
  font-weight: 700;
}

.notes-scroll {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 4px;
}

.note-item {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 10px 12px;
}

.note-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 5px;
  font-size: 0.76rem;
  color: #8aa0aa;
}

.note-meta strong {
  color: #cfd8dc;
  font-weight: 600;
}

.note-text {
  color: #fff;
  font-size: 0.88rem;
  line-height: 1.45;
  word-break: break-word;
  white-space: pre-wrap;
}

.notes-state {
  padding: 16px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
  color: #8aa0aa;
  text-align: center;
  font-size: 0.85rem;
}

.note-entry {
  display: flex;
  align-items: center;
  gap: 10px;
}

.note-input {
  flex: 1 1 auto;
  min-width: 0;
}

.note-add-btn {
  flex: 0 0 auto;
  min-width: 84px;
  height: 48px;
}

/* ----- Name/Reference edit flow ----- */
.edit-fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Subtle "locked" look while the inputs are read-only (before Edit is pressed). */
.detail-name-field.is-locked :deep(.v-field) {
  background: rgba(0, 0, 0, 0.18);
}

.edit-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cfd8dc;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.edit-btn:hover {
  background: rgba(100, 255, 218, 0.08);
  border-color: rgba(100, 255, 218, 0.3);
  color: #fff;
}

.confirm-prompt {
  margin-right: auto;
  font-size: 0.85rem;
  font-weight: 600;
  color: #f0d19a;
}

.notes-scroll::-webkit-scrollbar {
  width: 6px;
}

.notes-scroll::-webkit-scrollbar-thumb {
  background: #3a4a52;
  border-radius: 3px;
}

.notes-scroll::-webkit-scrollbar-thumb:hover {
  background: var(--duallo-accent-muted);
}

@media (max-width: 720px) {
  .detail-body {
    grid-template-columns: 1fr;
  }

  .detail-col-notes {
    border-left: none;
    padding-left: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    padding-top: 16px;
  }
}

.form-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #f0b54a;
}

.detail-title {
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
}

.detail-image {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.detail-image-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Download the shown anomaly image (overlay, top-right). */
.detail-image-dl {
  position: absolute;
  right: 10px;
  top: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 9px;
  cursor: pointer;
  background: rgba(10, 15, 18, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #e6edf0;
  backdrop-filter: blur(4px);
  transition: background 0.15s ease;
}
.detail-image-dl:hover { background: rgba(10, 15, 18, 0.9); }

/* Media action bar — Download / View Full Image / Street view. Attached flush to the bottom of the
   review image (shares its dark ground + rounded bottom) so it reads as the image's own control
   footer rather than a detached strip. Each cell is disabled when not available for the finding. */
.rc-media-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  background: linear-gradient(180deg, #121b1f, #0e1619);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0 0 10px 10px;
  overflow: hidden;
}
.rc-media-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 10px 6px;
  background: transparent;
  border: none;
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  color: #9fb2bb;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.rc-media-btn:last-child { border-right: none; }
.rc-media-btn:hover:not(:disabled) { background: rgba(100, 255, 218, 0.09); color: #64ffda; }
.rc-media-btn:disabled { opacity: 0.32; cursor: not-allowed; }

.detail-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 10px;
}

.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.detail-row dt {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.detail-row dd {
  margin: 0;
  color: #e3eef0;
  font-size: 0.9rem;
  text-align: right;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* When a cell holds the inline editor, let it breathe (and left-align the field). */
.detail-row dd:has(.rc-inline-row) { overflow: visible; text-align: left; }
.detail-row dd .rc-ref-value { justify-content: flex-end; }

.inline-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 138, 128, 0.08);
  border: 1px solid rgba(255, 138, 128, 0.2);
  color: #ffb4ab;
  font-size: 0.85rem;
}

.review-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: 10px;
  background: rgba(240, 181, 74, 0.06);
  border: 1px solid rgba(240, 181, 74, 0.22);
}

.review-label {
  font-size: 0.85rem;
  color: #f0d19a;
  font-weight: 600;
}

.review-actions {
  display: flex;
  gap: 10px;
}

.review-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1 1 0;
  padding: 9px 14px;
  border-radius: 10px;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.review-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.review-btn.approve {
  background: rgba(74, 222, 128, 0.14);
  border: 1px solid rgba(74, 222, 128, 0.4);
  color: #7ee6a0;
}

.review-btn.approve:hover:not(:disabled) {
  background: rgba(74, 222, 128, 0.22);
}

.review-btn.reject {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: #ff9a8f;
}

.review-btn.reject:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.2);
}

.detail-name-field :deep(.v-field) {
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
}

.detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.text-btn {
  padding: 10px 18px;
  border-radius: 10px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cfd8dc;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.text-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.primary-btn {
  padding: 10px 22px;
  border-radius: 10px;
  background: var(--duallo-accent-gradient);
  color: #071514;
  font-weight: 700;
  border: none;
  font: inherit;
  cursor: pointer;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.primary-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ══════════════════ Review workspace (redesign) ══════════════════ */
.anom { display: flex; flex-direction: column; gap: 12px; min-width: 0; width: 100%; height: 100%; min-height: 0; }

/* ═══════════ Mobile review flow ═══════════ */
.anom-m { display: flex; flex-direction: column; gap: 14px; height: 100%; min-height: 0; color: #eef3f2; }
.am-top { display: flex; align-items: center; gap: 10px; }
.am-back {
  flex: 0 0 auto; width: 44px; height: 44px; border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.12); background: rgba(255, 255, 255, 0.04);
  color: #eef3f2; display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
}
.am-title { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
.am-title-main { font-size: 1.05rem; font-weight: 700; color: #fff; }
.am-title-sub { font-size: 0.78rem; color: #8aa0aa; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.am-top-report {
  flex: 0 0 auto; width: 44px; height: 44px; border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12); background: rgba(255, 255, 255, 0.04);
  color: var(--duallo-accent-muted); display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
}
.am-top-report:active { background: rgba(255, 255, 255, 0.1); }

/* Filters */
.am-filters { display: flex; flex-direction: column; gap: 10px; flex: 0 0 auto; }
.am-tabs { display: flex; gap: 6px; overflow-x: auto; scrollbar-width: none; }
.am-tabs::-webkit-scrollbar { display: none; }
.am-tab {
  flex: 0 0 auto; display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 12px; border-radius: 10px; font: inherit; font-size: 0.85rem; font-weight: 600;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); color: #b0bec5; cursor: pointer;
}
.am-tab.active { background: rgba(100, 255, 218, 0.12); border-color: rgba(100, 255, 218, 0.35); color: var(--duallo-accent); }
.am-tab-count { font-size: 0.75rem; font-weight: 700; opacity: 0.9; }
.am-tab-count.is-approved { color: var(--duallo-status-approved); }
.am-tab-count.is-rejected { color: var(--duallo-status-rejected); }
.am-tab-count.is-pending { color: var(--duallo-status-pending); }
.am-filter-row { display: flex; gap: 8px; }
.am-search { position: relative; flex: 1 1 auto; display: flex; align-items: center; }
.am-search-icon { position: absolute; left: 11px; color: #6a7d85; pointer-events: none; }
.am-search input {
  width: 100%; height: 42px; padding: 0 12px 0 34px; border-radius: 10px;
  background: rgba(0, 0, 0, 0.22); border: 1px solid rgba(255, 255, 255, 0.1); color: #eceff1; font: inherit; font-size: 0.9rem;
}
.am-search input::placeholder { color: #6a7d85; }
.am-search input:focus { outline: none; border-color: rgba(102, 199, 180, 0.5); }
.am-type-select { flex: 0 0 auto; height: 42px; max-width: 42%; }
.am-tag-filter :deep(.v-field) { min-height: 42px; }   /* match the 42px mobile pill height */

/* List */
.am-list { flex: 1 1 auto; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 8px; padding: 2px 2px 8px; }
.am-item {
  display: grid; grid-template-columns: 46px 1fr auto; align-items: center; gap: 12px;
  width: 100%; text-align: left; font: inherit; padding: 10px; border-radius: 12px; cursor: pointer;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06);
}
.am-item:active { background: rgba(255, 255, 255, 0.06); }
.am-item.is-skeleton { pointer-events: none; }
.am-item-thumb {
  width: 46px; height: 46px; border-radius: 9px; overflow: hidden; background: #1c272c; flex: 0 0 auto;
  display: flex; align-items: center; justify-content: center;
}
.am-item-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.am-item-main { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.am-item-name { display: flex; align-items: center; gap: 7px; min-width: 0; }
.am-item-dot { width: 9px; height: 9px; border-radius: 50%; flex: 0 0 auto; }
.am-item-name-text { font-size: 0.95rem; font-weight: 700; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.am-item-sub { font-size: 0.78rem; color: #8aa0aa; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.am-item .sk-pill { width: 64px; height: 22px; border-radius: 999px; }

.am-done { flex: 1 1 auto; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; text-align: center; padding: 24px; }
.am-done-icon { width: 64px; height: 64px; border-radius: 32px; background: rgba(74, 222, 128, 0.16); display: flex; align-items: center; justify-content: center; }
.am-done-title { font-size: 1.35rem; font-weight: 700; color: #fff; margin: 0; }
.am-done-sub { font-size: 0.9rem; color: #8aa0aa; margin: 0; max-width: 280px; }
.am-report {
  margin-top: 8px; height: 52px; padding: 0 24px; border-radius: 14px; border: none;
  background: var(--duallo-accent-gradient); color: #071514;
  display: inline-flex; align-items: center; gap: 8px; font: inherit; font-size: 1rem; font-weight: 700; cursor: pointer;
}
.am-empty { flex: 1 1 auto; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #8aa0aa; }

/* Header */
.anom-head { display: flex; flex-direction: column; gap: 10px; }
.crumbs { display: flex; align-items: center; gap: 6px; font-size: 0.82rem; color: #8aa0aa; }
.crumb { color: #8aa0aa; }
.crumb-link { background: none; border: none; padding: 0; font: inherit; color: #8aa0aa; cursor: pointer; }
.crumb-link:hover { color: #cfd8dc; text-decoration: underline; }
.crumb-current { color: #cfd8dc; font-weight: 600; }
.crumb-sep { color: #4a5a60; }
.anom-head-row { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; }
.anom-title { font-size: clamp(1.5rem, calc(14px + 0.9vw), 2rem); font-weight: 800; color: #fff; margin: 0; line-height: 1.1; }
.anom-count { margin: 4px 0 0; font-size: 0.9rem; color: #8aa0aa; }

/* Filter bar */
.filter-bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.tab-group {
  display: inline-flex; align-items: center; gap: 2px;
  background: rgba(0, 0, 0, 0.22); border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px; padding: 3px;
}
.tab-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 7px 13px; border: none; border-radius: 9px; background: none;
  font: inherit; font-size: 0.86rem; font-weight: 600; color: #b0bec5; cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.tab-btn:hover { color: #fff; }
.tab-btn.active { background: rgba(255, 255, 255, 0.08); color: #fff; }
.tab-count {
  min-width: 20px; padding: 0 6px; height: 18px; border-radius: 999px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 0.72rem; font-weight: 700; background: rgba(255, 255, 255, 0.08); color: #cfd8dc;
}
.tab-count.is-approved { background: rgba(74, 222, 128, 0.16); color: #6ee7a0; }
.tab-count.is-rejected { background: rgba(248, 113, 113, 0.16); color: #ff9c9c; }
.tab-count.is-pending { background: rgba(245, 166, 35, 0.16); color: #f0b968; }

.pill-select {
  height: 38px; padding: 0 30px 0 12px; border-radius: 10px;
  background: rgba(0, 0, 0, 0.22) url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238aa0aa' stroke-width='2'><path d='M6 9l6 6 6-6'/></svg>") no-repeat right 9px center;
  border: 1px solid rgba(255, 255, 255, 0.1); color: var(--duallo-text-secondary); font: inherit; font-size: 0.85rem;
  cursor: pointer; -webkit-appearance: none; -moz-appearance: none; appearance: none;
  /* color-scheme:dark makes the native option popup render dark (matching the panel) instead of the
     OS-default white; the option rule below covers browsers that ignore it. */
  color-scheme: dark;
  outline: none; transition: border-color 0.2s;
}
.pill-select:hover, .pill-select:focus { border-color: rgba(102, 199, 180, 0.5); }
.pill-select option { background: var(--duallo-surface); color: var(--duallo-text-secondary); }
.search-wrap { position: relative; flex: 1 1 220px; min-width: 180px; display: flex; align-items: center; }
.search-wrap .search-icon { position: absolute; left: 11px; color: #6a7d85; pointer-events: none; }
.search-wrap .search-input {
  width: 100%; height: 38px; padding: 0 12px 0 34px; border-radius: 10px;
  background: rgba(0, 0, 0, 0.22); border: 1px solid rgba(255, 255, 255, 0.1);
  color: #eceff1; font: inherit; font-size: 0.9rem;
}
.search-wrap .search-input::placeholder { color: #6a7d85; }
.search-wrap .search-input:focus { outline: none; border-color: rgba(102, 199, 180, 0.5); }

/* Bulk bar */
.bulk-bar {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 8px 14px; border-radius: 10px;
  background: rgba(100, 199, 180, 0.08); border: 1px solid rgba(102, 199, 180, 0.28);
}
.bulk-count { font-size: 0.85rem; font-weight: 600; color: #cfead9; }
.bulk-actions { display: flex; gap: 8px; }
.bulk-btn { padding: 6px 14px; border-radius: 8px; border: 1px solid transparent; font: inherit; font-size: 0.82rem; font-weight: 700; cursor: pointer; }
.bulk-btn.approve { background: #34d399; color: #06231b; }
.bulk-btn.reject { background: rgba(248, 113, 113, 0.15); border-color: rgba(248, 113, 113, 0.4); color: #ff9c9c; }
.bulk-btn.ghost { background: rgba(255, 255, 255, 0.05); border-color: rgba(255, 255, 255, 0.1); color: #cfd8dc; }
.bulk-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Workspace layout — LEFT column holds the title + all controls + the list; the RIGHT column is
   the detail + supporting data (frame + minimap), capped so it never dwarfs the page. */
/* Single column by default (list full width). The persistent two-pane is only ≥1280 (below that the
   review surface is a pop-out drawer, so the list stays full width). */
.anom-body { display: grid; grid-template-columns: minmax(0, 1fr); gap: 18px; min-width: 0; flex: 1 1 auto; min-height: 0; align-items: stretch; }
@media (min-width: 1280px) {
  .anom-body.with-rail { grid-template-columns: minmax(0, 1fr) minmax(380px, 640px); }
}
.list-col { display: flex; flex-direction: column; gap: 12px; min-width: 0; min-height: 0; }
.list-col .anom-head-row { align-items: flex-start; }
.list-col .anom-title { font-size: clamp(1.4rem, calc(13px + 0.7vw), 1.7rem); }
.list-col .filter-bar { gap: 8px; flex: 0 0 auto; }
.list-col .search-wrap { flex: 0 0 auto; width: 100%; }
.list-col .anom-head-row, .list-col .scope-bar, .list-col .bulk-bar { flex: 0 0 auto; }
/* Never let the tab pills push the page wider than its column. */
.list-col .tab-group { min-width: 0; max-width: 100%; overflow-x: auto; scrollbar-width: none; }
.list-col .tab-group::-webkit-scrollbar { display: none; }
.list-col .tab-btn { flex: 0 0 auto; }

/* Triage list */
.list-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 14px;
  display: flex; flex-direction: column; min-width: 0; overflow: hidden;
  flex: 1 1 auto; min-height: 0;
}
.list-head {
  display: grid; grid-template-columns: 26px 1fr 130px 96px; align-items: center; gap: 10px;
  padding: 11px 16px; border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #8aa0aa;
}
.lh-status { text-align: left; }
.list-scroll {
  flex: 1 1 auto; min-height: 0; padding: 8px;
  display: flex; flex-direction: column; gap: 6px;
}

.anom-row {
  display: grid; grid-template-columns: 26px 44px 1fr 130px 96px; align-items: center; gap: 10px;
  width: 100%; flex: 0 0 auto; padding: 8px 8px; text-align: left; font: inherit;
  background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.05);
  border-left: 3px solid transparent; border-radius: 10px; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, grid-template-columns 0.18s ease;
}
/* Hover grows the thumbnail column (and the thumb) to double size for a quick preview. */
.anom-row:not(.is-skeleton):hover { background: rgba(255, 255, 255, 0.05); grid-template-columns: 26px 88px 1fr 130px 96px; }
.anom-row.is-current {
  background: rgba(102, 199, 180, 0.08);
  border-color: rgba(102, 199, 180, 0.4); border-left-color: var(--duallo-accent-muted);
}
.anom-row.is-skeleton { cursor: default; pointer-events: none; }
.ar-check { display: flex; align-items: center; justify-content: center; }
.chk { width: 15px; height: 15px; accent-color: var(--duallo-accent-muted); cursor: pointer; }
.ar-thumb {
  width: 44px; height: 40px; border-radius: 7px; overflow: hidden; background: #1c272c;
  display: flex; align-items: center; justify-content: center; position: relative;
  transition: width 0.18s ease, height 0.18s ease;
}
.anom-row:not(.is-skeleton):hover .ar-thumb { width: 88px; height: 80px; }
.ar-thumb-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ar-main { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.ar-name-row { display: flex; align-items: center; gap: 7px; min-width: 0; }
.ar-dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; }
.ar-name { font-size: 0.9rem; font-weight: 700; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ar-sub { font-size: 0.75rem; color: #8aa0aa; font-family: ui-monospace, Menlo, Consolas, monospace; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ar-ref { min-width: 0; }
.ar-ref .ref-text { font-size: 0.82rem; color: #cfd8dc; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block; }
.add-ref {
  background: none; border: 1px dashed rgba(255, 255, 255, 0.18); border-radius: 7px;
  padding: 3px 10px; font: inherit; font-size: 0.78rem; color: #8aa0aa; cursor: pointer;
}
.add-ref:hover { color: #cfd8dc; border-color: rgba(255, 255, 255, 0.3); }
/* In the list, "Add ref" is noise until you hover the row (or it's the current one). */
.anom-row .add-ref { opacity: 0; transition: opacity 0.12s ease; }
.anom-row:hover .add-ref, .anom-row.is-current .add-ref { opacity: 1; }
.ar-status { display: flex; }

/* Skeleton bits */
.sk { position: relative; overflow: hidden; background: rgba(255, 255, 255, 0.05); border-radius: 6px; }
.sk::after {
  content: ""; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
  animation: anom-sk 1.25s ease-in-out infinite;
}
@keyframes anom-sk { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) { .sk::after { animation: none; } }
.sk-line { display: block; height: 0.85rem; }
.sk-pill { width: 62px; height: 20px; border-radius: 999px; }

/* Review rail — one supporting element: frame · details · spatial context. */
.anom-rail {
  display: flex; flex-direction: column; gap: 16px; min-width: 0; min-height: 0;
  overflow-y: auto; overflow-x: hidden;
  scrollbar-width: thin; scrollbar-color: #3a4a52 transparent;
}
.anom-rail::-webkit-scrollbar { width: 6px; }
.anom-rail::-webkit-scrollbar-track { background: transparent; }
.anom-rail::-webkit-scrollbar-thumb { background: #3a4a52; border-radius: 3px; }
.support-panel {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 14px;
  display: flex; flex-direction: column; overflow: hidden; min-width: 0; max-width: 100%;
  /* Keep the panel at its content height so the rail (overflow-y:auto) scrolls it — without this the
     flex column shrinks the panel to fit, clipping the image at the top and killing the scroll. */
  flex: 0 0 auto;
}
/* Spatial-context section, divided from the details above it but inside the same card. */
.support-map { padding: 12px; border-top: 1px solid rgba(255, 255, 255, 0.06); }
.mini-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
.seg { display: inline-flex; background: rgba(0, 0, 0, 0.28); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 9px; padding: 2px; }
.seg button {
  padding: 6px 13px; border: none; border-radius: 7px; background: none;
  font: inherit; font-size: 0.8rem; font-weight: 600; color: #8aa0aa; cursor: pointer;
}
.seg button.active { background: rgba(255, 255, 255, 0.1); color: #fff; }
.mini-full {
  width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center;
  border-radius: 8px; background: rgba(0, 0, 0, 0.28); border: 1px solid rgba(255, 255, 255, 0.08);
  color: #cfd8dc; cursor: pointer;
}
.mini-full:hover { background: rgba(100, 255, 218, 0.12); border-color: rgba(100, 255, 218, 0.3); color: #fff; }
.mini-stage {
  border-radius: 10px; overflow: hidden; background: rgba(0, 0, 0, 0.22); aspect-ratio: 16 / 10; min-height: 220px; max-height: 320px;
}
.mini-stage :deep(.minimap), .mini-full-stage :deep(.minimap) {
  background: none; border: none; box-shadow: none; border-radius: 0; padding: 0; height: 100%;
}
.mini-stage :deep(.minimap-head), .mini-full-stage :deep(.minimap-head) { display: none; }
.mini-stage :deep(.minimap-stage), .mini-full-stage :deep(.minimap-stage) { aspect-ratio: auto; height: 100%; }

/* Review panel */
.rc-frame {
  position: relative; width: 100%; aspect-ratio: 16 / 9; max-height: 46vh; background: #10181c;
  display: flex; align-items: center; justify-content: center; overflow: hidden;
}
.rc-frame-img { width: 100%; height: 100%; object-fit: contain; display: block; background: #10181c; }
.rc-frame-empty { display: flex; align-items: center; justify-content: center; }
.rc-frame-index {
  position: absolute; top: 10px; left: 10px; padding: 3px 9px; border-radius: 999px;
  background: rgba(10, 15, 18, 0.72); border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.06em; color: #cfd8dc; backdrop-filter: blur(3px);
}

.rc-detail { padding: 16px; display: flex; flex-direction: column; gap: 14px; }
.rc-detail-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.rc-detail-title { display: flex; align-items: center; gap: 9px; min-width: 0; }
.rc-dot { width: 10px; height: 10px; border-radius: 50%; flex: 0 0 auto; }
.rc-name { font-size: 1.15rem; font-weight: 800; color: #fff; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.rc-meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 20px; margin: 0; }
.rc-meta-item { min-width: 0; }
.rc-meta-item dt { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #8aa0aa; margin-bottom: 2px; }
.rc-meta-item dd { margin: 0; font-size: 0.9rem; color: #e6edf0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* The reference cell can hold an inline editor, so let it wrap/overflow instead of clipping. */
.rc-meta-ref dd { overflow: visible; white-space: normal; }
.rc-mono { font-family: ui-monospace, Menlo, Consolas, monospace; }

/* ── Inline field editing (pencil next to a value) ── */
.rc-edit-icon {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; margin-left: 6px; border-radius: 6px;
  background: none; border: none; color: #7f939d; cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.rc-edit-icon:hover { background: rgba(255, 255, 255, 0.08); color: var(--duallo-accent); }
.rc-ref-value { display: inline-flex; align-items: center; gap: 2px; min-width: 0; max-width: 100%; }
.rc-ref-value > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rc-ref-value .muted { color: #5f727a; }

.rc-inline-row { display: inline-flex; align-items: center; gap: 6px; width: 100%; }
.rc-inline-input {
  flex: 1 1 auto; min-width: 0; height: 34px; padding: 0 10px; border-radius: 8px;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(100, 255, 218, 0.4); color: #fff;
  font: inherit; font-size: 0.9rem;
}
.rc-inline-input:focus { outline: none; border-color: var(--duallo-accent); background: rgba(255, 255, 255, 0.07); }
.rc-inline-input::placeholder { color: #7f939d; }
.rc-name-input { font-size: 1rem; font-weight: 700; height: 36px; }
.rc-inline-btn {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 8px; cursor: pointer;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.14); color: #cfd8dc;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.rc-inline-btn.save:hover:not(:disabled) { background: rgba(52, 211, 153, 0.16); border-color: rgba(52, 211, 153, 0.5); color: #6ee7a0; }
.rc-inline-btn.cancel:hover:not(:disabled) { background: rgba(248, 113, 113, 0.14); border-color: rgba(248, 113, 113, 0.45); color: #ff9c9c; }
.rc-inline-btn:disabled { opacity: 0.55; cursor: not-allowed; }

.rc-3d {
  align-self: flex-start; display: inline-flex; align-items: center; gap: 4px;
  background: none; border: none; padding: 0; font: inherit; font-size: 0.88rem; font-weight: 600; color: var(--duallo-accent-muted); cursor: pointer;
}
.rc-3d:hover { color: #7ce0cc; }
.rc-actions { display: grid; grid-template-columns: 1fr 1.7fr; gap: 12px; margin-top: 2px; }
.rc-btn { padding: 13px; border-radius: 11px; border: 1px solid transparent; font: inherit; font-size: 0.95rem; font-weight: 700; cursor: pointer; }
.rc-btn.reject { background: rgba(255, 255, 255, 0.04); border-color: rgba(255, 255, 255, 0.12); color: #cfd8dc; }
.rc-btn.reject:hover:not(:disabled) { background: rgba(248, 113, 113, 0.12); border-color: rgba(248, 113, 113, 0.4); color: #ff9c9c; }
.rc-btn.approve { background: #34d399; color: #06231b; }
.rc-btn.approve:hover:not(:disabled) { background: #2fbd88; }
.rc-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.rc-hints { display: flex; flex-wrap: wrap; gap: 4px 14px; font-size: 0.74rem; color: #8aa0aa; }

/* Review controls wrapper + "review complete" summary (with Change decision) in the rail. */
.rc-review { display: flex; flex-direction: column; gap: 8px; }
.rc-reviewed {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 12px 14px; border-radius: 11px;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08);
}
.rc-reviewed-text { display: inline-flex; align-items: center; gap: 8px; min-width: 0; font-size: 0.9rem; color: #cfd8dc; }
.rc-reviewed-text strong { color: #fff; }
.rc-change-btn {
  flex: 0 0 auto; padding: 8px 13px; border-radius: 9px; font: inherit; font-size: 0.82rem; font-weight: 700;
  background: rgba(100, 255, 218, 0.1); border: 1px solid rgba(100, 255, 218, 0.3); color: var(--duallo-accent); cursor: pointer;
  transition: background 0.15s ease;
}
.rc-change-btn:hover { background: rgba(100, 255, 218, 0.18); }

/* Review confirmation toast */
.review-toast { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; }

/* Notes / comments folded into the rail (own section, divided like the map). */
.rc-notes { padding: 14px 16px 16px; border-top: 1px solid rgba(255, 255, 255, 0.06); display: flex; flex-direction: column; gap: 10px; }
.mini-caption { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8aa0aa; }

/* Dialog "review complete" summary (mobile / fallback dialog). */
.review-done { flex-direction: row; align-items: center; justify-content: space-between; gap: 12px; }
.review-done-text { display: inline-flex; align-items: center; gap: 8px; font-size: 0.9rem; color: #cfd8dc; }
.review-done-text strong { color: #fff; }
.rc-hints kbd {
  display: inline-block; min-width: 15px; padding: 1px 5px; border-radius: 5px; text-align: center;
  background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12);
  color: #cfd8dc; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.72rem;
}

/* Empty / caught-up */
.rc-empty {
  align-items: center; justify-content: center; text-align: center; gap: 8px;
  padding: 40px 24px; color: #8aa0aa; min-height: 260px;
}
.caught-icon {
  width: 56px; height: 56px; border-radius: 16px; display: flex; align-items: center; justify-content: center;
  background: rgba(74, 222, 128, 0.1); border: 1px solid rgba(74, 222, 128, 0.28); margin-bottom: 4px;
}
.caught-title { margin: 0; font-size: 1.1rem; font-weight: 800; color: #cfd8dc; }
.caught-sub { margin: 0; font-size: 0.88rem; max-width: 240px; line-height: 1.45; }
.caught-report { margin-top: 8px; gap: 6px; min-width: 0; }

/* Fullscreen minimap */
.mini-full-modal {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 16px; padding: 14px;
}
.mini-full-stage { border-radius: 10px; overflow: hidden; background: rgba(0, 0, 0, 0.22); height: 70vh; }

/* Approve confirmation dialog */
.confirm-modal { text-align: center; padding: 26px 24px 20px; }
.confirm-icon {
  width: 52px; height: 52px; margin: 0 auto 14px; border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(52, 211, 153, 0.1); border: 1px solid rgba(52, 211, 153, 0.28);
}
.confirm-title { margin: 0 0 6px; font-size: 1.2rem; font-weight: 700; color: #fff; }
.confirm-text { margin: 0 0 22px; font-size: 0.92rem; color: #b0bec5; line-height: 1.5; }
.confirm-text strong { color: #fff; }
.confirm-actions { display: flex; gap: 10px; justify-content: center; }
.confirm-actions .text-btn, .confirm-actions .primary-btn { flex: 1 1 0; max-width: 160px; }

/* ── Responsive (laptop 125–150% OS scaling ⇒ narrower CSS viewport) ── */
/* Drop the list's reference column before it crowds; keep the rows readable. */
@media (max-width: 1400px) {
  .list-head { grid-template-columns: 24px 1fr 84px; }
  .anom-row { grid-template-columns: 24px 40px 1fr 84px; gap: 8px; }
  .lh-ref, .ar-ref { display: none; }
}
/* Tablet-landscape (960–1279): the review surface is a dismissible right-hand drawer over a dimmed
   backdrop, so the list stays full-width underneath (no stacked-below rail). */
.anom-drawer-backdrop {
  position: fixed; inset: 0; z-index: 55; background: rgba(0, 0, 0, 0.5);
  animation: anom-backdrop-in 0.2s ease;
}
@keyframes anom-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
.anom-rail--drawer {
  position: fixed; top: 0; right: 0; bottom: 0; z-index: 60;
  width: min(460px, 92vw);
  padding: 14px;
  background: var(--duallo-bg-app);
  box-shadow: -12px 0 40px rgba(0, 0, 0, 0.5);
  overflow-y: auto;
  animation: anom-drawer-in 0.22s ease;
}
@keyframes anom-drawer-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
.anom-drawer-close {
  position: absolute; top: 12px; right: 12px; z-index: 2;
  width: 34px; height: 34px; border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(10, 15, 18, 0.6); border: 1px solid rgba(255, 255, 255, 0.16); color: #eef3f2;
  backdrop-filter: blur(4px);
}
.anom-drawer-close:hover { background: rgba(10, 15, 18, 0.8); color: #fff; }

/* Note: there is no <720px rule here — the desktop workspace only renders at ≥960 (below that the
   dedicated .anom-m mobile template takes over), so such a rule would never match. */
</style>

<style>
/* Mobile bottom-sheet presentation of the anomaly detail dialog. Global (not scoped) because the
   dialog content is teleported to <body>, so the overlay-content element carries no scope attribute. */
.anom-detail-sheet.v-overlay__content {
  position: fixed; left: 0; right: 0; bottom: 0; top: auto;
  width: 100%; max-width: 100%; margin: 0;
}
.anom-detail-sheet .detail-modal {
  border-radius: 18px 18px 0 0;
  max-height: 92dvh;
  width: 100%;
  overflow-y: auto;
}
</style>
